import { Section } from './Section';
import {
  Bloodwork,
  PanelRange,
  PanelField,
  PanelMarker,
  ProbabilityCategory,
  RedBloodCellSize,
  RedBloodCellColor,
  PossibleRootCauseCategory,
  RootCause,
  RootCauseCategory,
  BoneMarrowAnalysis,
  ElectrolytesAnalysis,
  GlucoseAnalysis,
  HormonesAnalysis,
  InflammationOxidativeStressAnalysis,
  IronAnalysis,
  KidneyAnalysis,
  LipidsAnalysis,
  LiverBiliaryAnalysis,
  NutrientsAnalysis,
  PlateletsAnalysis,
  ProteinsAnalysis,
  ThyroidAnalysis,
  WBCAnalysis,
  ProstateAnalysis, //12-10-25 MA
} from '.';

// TODO: Break this class up in to something like the following:
// DehydrationAnalysis
// AnemiaAnalysis
// RedBloodCellSizeAnalysis
// RedBloodCellColorAnalysis
// PossibleRootCausesAnalysis

export class BloodworkAnalysis {
  bloodwork: Bloodwork;
  bloodworkRanges: PanelRange[];
  bloodworkFields: PanelField[];
  panelMarkers?: PanelMarker[];

  // CBC PanelMarkers
  wbc?: PanelMarker;
  rbc?: PanelMarker;
  hgb?: PanelMarker;
  hct?: PanelMarker;
  mcv?: PanelMarker;
  mch?: PanelMarker;
  mchc?: PanelMarker;
  rdw?: PanelMarker;
  platelets?: PanelMarker;
  mpv?: PanelMarker;

  // WBC Differencials (Absolute)
  neutrophilsAbs?: PanelMarker;
  lymphocytesAbs?: PanelMarker;
  monocytesAbs?: PanelMarker;
  eosinophilsAbs?: PanelMarker;
  basophilsAbs?: PanelMarker;

  // WBC Differencials (Percentage)
  neutrophils?: PanelMarker;
  lymphocytes?: PanelMarker;
  monocytes?: PanelMarker;
  eosinophils?: PanelMarker;
  basophils?: PanelMarker;

  nlr?: PanelMarker;

  // Iron PanelMarkers
  iron?: PanelMarker;
  tibc?: PanelMarker;
  saturation?: PanelMarker;
  ferritin?: PanelMarker;
  transferrin?: PanelMarker;

  // CMP markers
  glucose?: PanelMarker;
  hemoglobinA1c?: PanelMarker;
  bun?: PanelMarker;
  creatinine?: PanelMarker;
  eGFR?: PanelMarker;
  bunCreatinineRatio?: PanelMarker;
  sodium?: PanelMarker;
  potassium?: PanelMarker;
  chloride?: PanelMarker;
  carbonDioxide?: PanelMarker;
  calcium?: PanelMarker;
  protein?: PanelMarker;
  albumin?: PanelMarker;
  globulin?: PanelMarker;
  albuminGlobulinRatio?: PanelMarker;
  bilirubin?: PanelMarker;
  alp?: PanelMarker;
  ast?: PanelMarker;
  alt?: PanelMarker;
  // Calculated Markers
  anionGap?: PanelMarker;
  anionGapWithoutK?: PanelMarker;
  rpi?: PanelMarker;
  sodiumPotassiumRatio?: PanelMarker;

  // Lipid Panel Markers
  cholesterol?: PanelMarker;
  hdl?: PanelMarker;
  triglycerides?: PanelMarker;
  ldl?: PanelMarker;
  cholesterolHDLRatio?: PanelMarker;
  triglyceridesHDLRatio?: PanelMarker;
  apolipoproteinA1?: PanelMarker;
  apolipoproteinB?: PanelMarker;
  apoBapoA1Ratio?: PanelMarker;
  lipoproteinA?: PanelMarker;

  // Additional Panel Markers
  amylase?: PanelMarker;
  bileAcids?: PanelMarker;
  bilirubinDirect?: PanelMarker;
  bilirubinIndirect?: PanelMarker;
  ceruloplasmin?: PanelMarker;
  copperRBCμgmL?: PanelMarker;
  copperRBCμgdL?: PanelMarker;
  copperSerumμgdL?: PanelMarker;
  copperSerummcgmL?: PanelMarker;
  cpeptide?: PanelMarker;
  dDimer?: PanelMarker;
  fibrinogen?: PanelMarker;
  folateRBC?: PanelMarker;
  folateSerum?: PanelMarker;
  ggt?: PanelMarker;
  homocysteine?: PanelMarker;
  hsCRP?: PanelMarker;
  esr?: PanelMarker;
  insulin?: PanelMarker;
  lactateDehydrogenase?: PanelMarker;
  magnesiumRBC?: PanelMarker;
  magnesium?: PanelMarker;
  phosphorus?: PanelMarker;
  psaTotal?: PanelMarker; //12-10-25 MA
  psaFree?: PanelMarker; //12-10-25 MA
  psaFreePercent?: PanelMarker; //12-10-25 MA
  reticulocyteCount?: PanelMarker;
  uricAcid?: PanelMarker;
  vitaminA?: PanelMarker;
  vitaminB6?: PanelMarker;
  vitaminB12?: PanelMarker;
  vitaminD?: PanelMarker;
  vitaminD125?: PanelMarker;
  zincRBCμgdL?: PanelMarker;
  zincRBCmgL?: PanelMarker;
  zincSerumμgdL?: PanelMarker;
  zincSerummcgmL?: PanelMarker;
  copperZincRatio?: PanelMarker;
  percentOfFreeCopper?: PanelMarker;

  // Thyroid Panel Markers
  tsh?: PanelMarker;
  t4Total?: PanelMarker;
  t4Free?: PanelMarker;
  t3Total?: PanelMarker;
  t3Free?: PanelMarker;
  reverseT3?: PanelMarker;
  tpoAb?: PanelMarker;
  tgAb?: PanelMarker;
  tbg?: PanelMarker;
  t3Uptake?: PanelMarker;
  freeT4Index?: PanelMarker;

  // Hormones Panel Markers;
  menstrualPhase?: PanelMarker;
  antiMullerianHormone?: PanelMarker;
  cortisolAM?: PanelMarker;
  cortisolPM?: PanelMarker;
  dheaS?: PanelMarker;
  dihydrotestosterone?: PanelMarker;
  estradiolE2?: PanelMarker;
  estriolE3?: PanelMarker;
  estrogensTotal?: PanelMarker;
  estroneE1?: PanelMarker;
  fsh?: PanelMarker;
  hCG?: PanelMarker;
  igf1?: PanelMarker;
  lh?: PanelMarker;
  pregnenolone?: PanelMarker;
  progesterone?: PanelMarker;
  hydroxyprogesterone17?: PanelMarker;
  prolactin?: PanelMarker;
  prolactinLactating?: PanelMarker;
  shbg?: PanelMarker;
  testosteroneBioavailable?: PanelMarker;
  testosteroneBioavailablePercent?: PanelMarker;
  testosteroneFreeCalc?: PanelMarker;
  testosteroneFreeDirect?: PanelMarker;
  testosteroneTotal?: PanelMarker;
  testosteroneTotalLCMS?: PanelMarker;

  // Analysis values
  anemicProbability?: number;
  anemicProbabilityCategory?: ProbabilityCategory;
  redBloodCellSize?: RedBloodCellSize;
  redBloodCellColor?: RedBloodCellColor;
  nutrientDeficiencyOrInflammationLikelyText?: string;
  dehydrationProbabilityCategory?: ProbabilityCategory;
  acuteDehydrationProbabilityCategory?: ProbabilityCategory;
  chronicDehydrationProbabilityCategory?: ProbabilityCategory;
  poorOxygenationProbabilityCategory?: ProbabilityCategory;

  // This is for anemia
  possibleRootCauseCategories?: PossibleRootCauseCategory[];

  boneMarrowAnalysis?: BoneMarrowAnalysis;
  electrolytesAnalysis?: ElectrolytesAnalysis;
  glucoseAnalysis?: GlucoseAnalysis;
  hormonesAnalysis?: HormonesAnalysis;
  inflammationOxidativeStressAnalysis?: InflammationOxidativeStressAnalysis;
  ironAnalysis?: IronAnalysis;
  kidneyAnalysis?: KidneyAnalysis;
  lipidsAnalysis?: LipidsAnalysis;
  liverBiliaryAnalysis?: LiverBiliaryAnalysis;
  nutrientsAnalysis?: NutrientsAnalysis;
  plateletsAnalysis?: PlateletsAnalysis;
  proteinsAnalysis?: ProteinsAnalysis;
  thyroidAnalysis?: ThyroidAnalysis;
  wbcAnalysis?: WBCAnalysis;
  prostateAnalysis?: ProstateAnalysis; //12-10-25 MA

  COMMON_IRON_TIBC_SATURATION_FERRITIN?: boolean;

  constructor(bloodworkAnalysis?: Partial<BloodworkAnalysis>) {
    if (!bloodworkAnalysis) {
      return;
    }
    Object.keys(bloodworkAnalysis).forEach(key => {
      this[key] = bloodworkAnalysis[key];
    });
    if (bloodworkAnalysis.bloodwork) {
      this.bloodwork = new Bloodwork(bloodworkAnalysis.bloodwork);
    }
  }

  init?(): BloodworkAnalysis {
    this.panelMarkers = [];
    this.populatePanelMarkers();

    this.boneMarrowAnalysis = new BoneMarrowAnalysis().init(this);
    this.electrolytesAnalysis = new ElectrolytesAnalysis().init(this);
    this.glucoseAnalysis = new GlucoseAnalysis().init(this);
    this.hormonesAnalysis = new HormonesAnalysis().init(this);
    this.inflammationOxidativeStressAnalysis =
      new InflammationOxidativeStressAnalysis().init(this);
    this.ironAnalysis = new IronAnalysis().init(this);
    this.kidneyAnalysis = new KidneyAnalysis().init(this);
    this.lipidsAnalysis = new LipidsAnalysis().init(this);
    this.liverBiliaryAnalysis = new LiverBiliaryAnalysis().init(this);
    this.nutrientsAnalysis = new NutrientsAnalysis().init(this);
    this.plateletsAnalysis = new PlateletsAnalysis().init(this);
    this.proteinsAnalysis = new ProteinsAnalysis().init(this);
    this.thyroidAnalysis = new ThyroidAnalysis().init(this);
    this.wbcAnalysis = new WBCAnalysis().init(this) as WBCAnalysis;
    this.prostateAnalysis = new ProstateAnalysis().init(this); //12-10-25 MA

    this.reset();

    this.COMMON_IRON_TIBC_SATURATION_FERRITIN =
      (this.iron.isBlank() || this.iron.isLessThanOrIsOptimal()) &&
      (this.tibc.isBlank() || this.tibc.isLessThanOrIsOptimal()) &&
      (this.saturation.isBlank() || this.saturation.isLessThanOrIsOptimal()) &&
      this.ferritin.isGreaterThanOrIsOptimal();

    return this;
  }

  reset?() {
    this.anemicProbability = -1;
    this.anemicProbabilityCategory = ProbabilityCategory.NA;
    this.redBloodCellSize = RedBloodCellSize.NOT_APPLICABLE;
    this.redBloodCellColor = RedBloodCellColor.NOT_APPLICABLE;
    this.nutrientDeficiencyOrInflammationLikelyText = undefined;
    this.dehydrationProbabilityCategory = ProbabilityCategory.NA;
    this.acuteDehydrationProbabilityCategory = ProbabilityCategory.NA;
    this.chronicDehydrationProbabilityCategory = ProbabilityCategory.NA;
    this.poorOxygenationProbabilityCategory = ProbabilityCategory.NA;
    this.possibleRootCauseCategories = [];

    this.boneMarrowAnalysis.reset();
    this.electrolytesAnalysis.reset();
    this.glucoseAnalysis.reset();
    this.hormonesAnalysis.reset();
    this.inflammationOxidativeStressAnalysis.reset();
    this.ironAnalysis.reset();
    this.kidneyAnalysis.reset();
    this.lipidsAnalysis.reset();
    this.liverBiliaryAnalysis.reset();
    this.nutrientsAnalysis.reset();
    this.plateletsAnalysis.reset();
    this.proteinsAnalysis.reset();
    this.thyroidAnalysis.reset();
    this.wbcAnalysis.reset();
    this.prostateAnalysis.reset(); //12-10-25 MA
  }

  populatePanelMarkers?() {
    this.populateCBCPanelMarkers();
    this.populateWBCPanelMarkers();
    this.populateIronPanelMarkers();
    this.populateCMPPanelMarkers();
    this.populateLipidPanelMarkers();
    this.populateAnionGapPanelMarkers();
    this.populateAdditionalPanelMarkers();
    this.populateThyroidPanelMarkers();
    this.populateRPIPanelMarker();
    this.populateSexHormonePanelMarkers();
  }

  populatePanelMarkersArray?(): void {
    const panelMarkerKeys = Object.keys(this).filter(attribute => {
      return this[attribute] instanceof PanelMarker;
    });
    this.panelMarkers = panelMarkerKeys.map(attribute => {
      return this[attribute];
    });
  }

  findPanelMarker?(attribute: string): PanelMarker {
    return this.panelMarkers.find(panelMarker => {
      return panelMarker.field.id === attribute;
    });
  }

  preparePreviousPanelMarkers?(previousBloodworkAnalysis: BloodworkAnalysis) {
    if (!previousBloodworkAnalysis) {
      return;
    }
    this.populatePanelMarkers();
    this.populatePanelMarkersArray();
    // Note: previousBloodworkAnalysis should already be initialized with init() before calling this method
    previousBloodworkAnalysis.populatePanelMarkers();
    previousBloodworkAnalysis.populatePanelMarkersArray();

    this.panelMarkers.forEach(pm => {
      const previousPanelMarker = previousBloodworkAnalysis.findPanelMarker(
        pm.field.id,
      );
      if (previousPanelMarker) {
        pm.previousPanelMarker = previousPanelMarker;
      } else {
        pm.previousPanelMarker = undefined;
      }
    });
  }

  populateCBCPanelMarkers?() {
    this.rbc = new PanelMarker({
      attribute: 'redBloodCells',
      value: this.bloodwork.redBloodCells,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.hgb = new PanelMarker({
      attribute: 'hemoglobin',
      value: this.bloodwork.hemoglobin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.hct = new PanelMarker({
      attribute: 'hematocrit',
      value: this.bloodwork.hematocrit,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.mcv = new PanelMarker({
      attribute: 'meanCorpuscularVolume',
      value: this.bloodwork.meanCorpuscularVolume,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.mch = new PanelMarker({
      attribute: 'meanCorpuscularHGB',
      value: this.bloodwork.meanCorpuscularHGB,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.mchc = new PanelMarker({
      attribute: 'mchConcentration',
      value: this.bloodwork.mchConcentration,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.rdw = new PanelMarker({
      attribute: 'rbcDistributionWidth',
      value: this.bloodwork.rbcDistributionWidth,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.platelets = new PanelMarker({
      attribute: 'plateletCount',
      value: this.bloodwork.plateletCount,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  populateWBCPanelMarkers?() {
    this.wbc = new PanelMarker({
      attribute: 'whiteBloodCells',
      value: this.bloodwork.whiteBloodCells,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.mpv = new PanelMarker({
      attribute: 'meanPlateletVolume',
      value: this.bloodwork.meanPlateletVolume,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.neutrophilsAbs = new PanelMarker({
      attribute: 'neutrophilsAbsolute',
      value: this.bloodwork.percentageToAbsolute(this.bloodwork.neutrophils),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.lymphocytesAbs = new PanelMarker({
      attribute: 'lymphocytesAbsolute',
      value: this.bloodwork.percentageToAbsolute(this.bloodwork.lymphocytes),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.monocytesAbs = new PanelMarker({
      attribute: 'monocytesAbsolute',
      value: this.bloodwork.percentageToAbsolute(this.bloodwork.monocytes),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.eosinophilsAbs = new PanelMarker({
      attribute: 'eosinophilsAbsolute',
      value: this.bloodwork.percentageToAbsolute(this.bloodwork.eosinophils),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.basophilsAbs = new PanelMarker({
      attribute: 'basophilsAbsolute',
      value: this.bloodwork.percentageToAbsolute(this.bloodwork.basophils),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    // WBC Differencials (percentages)
    this.neutrophils = new PanelMarker({
      attribute: 'neutrophils',
      value: this.bloodwork.decimalToPercentage(this.bloodwork.neutrophils),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.lymphocytes = new PanelMarker({
      attribute: 'lymphocytes',
      value: this.bloodwork.decimalToPercentage(this.bloodwork.lymphocytes),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.monocytes = new PanelMarker({
      attribute: 'monocytes',
      value: this.bloodwork.decimalToPercentage(this.bloodwork.monocytes),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.eosinophils = new PanelMarker({
      attribute: 'eosinophils',
      value: this.bloodwork.decimalToPercentage(this.bloodwork.eosinophils),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.basophils = new PanelMarker({
      attribute: 'basophils',
      value: this.bloodwork.decimalToPercentage(this.bloodwork.basophils),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.nlr = new PanelMarker({
      attribute: 'nlr',
      value: this.calculateNLR(),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  calculateNLR?(): number {
    if (this.neutrophils.hasValue() && this.lymphocytes.hasValue()) {
      return this.neutrophils.value / this.lymphocytes.value;
    }
    return undefined;
  }

  populateIronPanelMarkers?() {
    this.iron = new PanelMarker({
      attribute: 'iron',
      value: this.bloodwork.iron,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.tibc = new PanelMarker({
      attribute: 'tibc',
      value: this.bloodwork.tibc,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.saturation = new PanelMarker({
      attribute: 'saturation',
      value: this.bloodwork.saturation,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.ferritin = new PanelMarker({
      attribute: 'ferritin',
      value: this.bloodwork.ferritin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.transferrin = new PanelMarker({
      attribute: 'transferrin',
      value: this.bloodwork.transferrin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  populateCMPPanelMarkers?() {
    this.glucose = new PanelMarker({
      attribute: 'glucose',
      value: this.bloodwork.glucose,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.hemoglobinA1c = new PanelMarker({
      attribute: 'hemoglobinA1c',
      value: this.bloodwork.hemoglobinA1c,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.sodium = new PanelMarker({
      attribute: 'sodium',
      value: this.bloodwork.sodium,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.potassium = new PanelMarker({
      attribute: 'potassium',
      value: this.bloodwork.potassium,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.chloride = new PanelMarker({
      attribute: 'chloride',
      value: this.bloodwork.chloride,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.carbonDioxide = new PanelMarker({
      attribute: 'carbonDioxide',
      value: this.bloodwork.carbonDioxide,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.albumin = new PanelMarker({
      attribute: 'albumin',
      value: this.bloodwork.albumin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    // Correct calcium if necessary
    // If albumin < optimal,
    // use this formula to correct
    // calcium = (0.8 mg/dL x [4.5 – albumin] ) + calcium
    let calcium = this.bloodwork.calcium;
    if (this.albumin.isStricklyLessThanOptimal()) {
      calcium = 0.8 * (4.5 - this.albumin.value) + calcium;
    }
    this.calcium = new PanelMarker({
      attribute: 'calcium',
      value: calcium,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.protein = new PanelMarker({
      attribute: 'protein',
      value: this.bloodwork.protein,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.bun = new PanelMarker({
      attribute: 'bun',
      value: this.bloodwork.bun,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.creatinine = new PanelMarker({
      attribute: 'creatinine',
      value: this.bloodwork.creatinine,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.bunCreatinineRatio = new PanelMarker({
      attribute: 'bunCreatinineRatio',
      value: this.bloodwork.bunCreatinineRatio,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    const eGFRAttribute = this.bloodwork.client.isAfricanAmerican()
      ? 'eGFRforAA'
      : 'eGFR';
    const eGFR = this.bloodwork.client.isAfricanAmerican()
      ? this.bloodwork.eGFRforAA
      : this.bloodwork.eGFR;

    this.eGFR = new PanelMarker({
      attribute: eGFRAttribute,
      value: eGFR,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.bilirubin = new PanelMarker({
      attribute: 'bilirubin',
      value: this.bloodwork.bilirubin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.globulin = new PanelMarker({
      attribute: 'globulin',
      value: this.bloodwork.globulin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.albuminGlobulinRatio = new PanelMarker({
      attribute: 'albuminGlobulinRatio',
      value: this.bloodwork.albuminGlobulinRatio,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.alp = new PanelMarker({
      attribute: 'alkalinePhosphatase',
      value: this.bloodwork.alkalinePhosphatase,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.ast = new PanelMarker({
      attribute: 'ast',
      value: this.bloodwork.ast,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.alt = new PanelMarker({
      attribute: 'alt',
      value: this.bloodwork.alt,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.sodiumPotassiumRatio = new PanelMarker({
      attribute: 'sodiumPotassiumRatio',
      value: this.calculateNaKRatio(),
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  calculateNaKRatio?(): number {
    if (this.sodium.hasValue() && this.potassium.hasValue()) {
      return this.sodium.value / this.potassium.value;
    }
    return undefined;
  }

  populateLipidPanelMarkers?() {
    this.cholesterol = new PanelMarker({
      attribute: 'cholesterol',
      value: this.bloodwork.cholesterol,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.hdl = new PanelMarker({
      attribute: 'hdl',
      value: this.bloodwork.hdl,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.triglycerides = new PanelMarker({
      attribute: 'triglycerides',
      value: this.bloodwork.triglycerides,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.ldl = new PanelMarker({
      attribute: 'ldl',
      value: this.bloodwork.ldl,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.cholesterolHDLRatio = new PanelMarker({
      attribute: 'cholesterolHDLRatio',
      value: this.bloodwork.cholesterolHDLRatio,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.triglyceridesHDLRatio = new PanelMarker({
      attribute: 'triglyceridesHDLRatio',
      value: this.bloodwork.triglyceridesHDLRatio,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.apolipoproteinA1 = new PanelMarker({
      attribute: 'apolipoproteinA1',
      value: this.bloodwork.apolipoproteinA1,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.apolipoproteinB = new PanelMarker({
      attribute: 'apolipoproteinB',
      value: this.bloodwork.apolipoproteinB,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.apoBapoA1Ratio = new PanelMarker({
      attribute: 'apoBapoA1Ratio',
      value: this.bloodwork.apoBapoA1Ratio,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.lipoproteinA = new PanelMarker({
      attribute: 'lipoproteinA',
      value: this.bloodwork.lipoproteinA,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  populateAnionGapPanelMarkers?() {
    let anionGap: number = undefined;
    let anionGapWithoutK: number = undefined;

    if (this.hasAnionGapValues()) {
      if (this.potassium.hasValue()) {
        anionGap =
          this.sodium.value +
          this.potassium.value -
          (this.chloride.value + this.carbonDioxide.value);
      }
      anionGapWithoutK =
        this.sodium.value - (this.chloride.value + this.carbonDioxide.value);

      if (this.albumin.isStricklyLessThanOptimal()) {
        anionGap = this.calculateAGAG(anionGap);
        anionGapWithoutK = this.calculateAGAG(anionGapWithoutK);
      }
    }

    // AG w K = [(Na + K) - (Cl + CO2)]
    // If Albumin < optimal, then Albumin Corrected Anion Gap (ACAG) = AG + 2.5*(4.4 - Albumin)
    this.anionGap = new PanelMarker({
      attribute: 'anionGap',
      value: anionGap,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    // AG w/o K = [(Na) - (Cl + CO2)]
    // If Albumin < optimal, then Albumin Corrected Anion Gap (ACAG) = AG + 2.5*(4.4 - Albumin)
    this.anionGapWithoutK = new PanelMarker({
      attribute: 'anionGapWithoutK',
      value: anionGapWithoutK,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  populateRPIPanelMarker?() {
    let rpi;
    let crc;
    if (this.reticulocyteCount.hasValue() && this.hct.hasValue() && this.hct.range?.mean) {
      // Reticulocytes x (HCT / Optimal Mean HCT / RPI Correction Factor)
      let rpiCorrectionFactor;
      const hct = this.hct.value;
      if (PanelRange.isInRange(hct, 0, 15)) {
        rpiCorrectionFactor = 2.5;
      } else if (PanelRange.isInRange(hct, 15.001, 25)) {
        rpiCorrectionFactor = 2;
      } else if (PanelRange.isInRange(hct, 25.001, 35)) {
        rpiCorrectionFactor = 1.5;
      } else if (PanelRange.isInRange(hct, 35.001, 75)) {
        rpiCorrectionFactor = 1;
      }
      crc =
        this.reticulocyteCount.value * (this.hct.value / this.hct.range.mean);
      rpi =
        this.reticulocyteCount.value *
        (this.hct.value / this.hct.range.mean / rpiCorrectionFactor);
    }
    // Set rpi
    this.rpi = new PanelMarker({
      attribute: 'RPI',
      value: rpi,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    // Recalculate reticulocyteCount with corrected value
    this.reticulocyteCount = new PanelMarker({
      attribute: 'reticulocyteCount',
      value: crc,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  populateAdditionalPanelMarkers?() {
    this.amylase = new PanelMarker({
      attribute: 'amylase',
      value: this.bloodwork.amylase,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.bileAcids = new PanelMarker({
      attribute: 'bileAcids',
      value: this.bloodwork.bileAcids,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.bilirubinDirect = new PanelMarker({
      attribute: 'bilirubinDirect',
      value: this.bloodwork.bilirubinDirect,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.bilirubinIndirect = new PanelMarker({
      attribute: 'bilirubinIndirect',
      value: this.bloodwork.bilirubinIndirect,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.ceruloplasmin = new PanelMarker({
      attribute: 'ceruloplasmin',
      value: this.bloodwork.ceruloplasmin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.copperRBCμgmL = new PanelMarker({
      attribute: 'copperRBCμgmL',
      value: this.bloodwork.copperRBCμgmL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.copperRBCμgdL = new PanelMarker({
      attribute: 'copperRBCμgdL',
      value: this.bloodwork.copperRBCμgdL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.copperSerumμgdL = new PanelMarker({
      attribute: 'copperSerumμgdL',
      value: this.bloodwork.copperSerumμgdL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.copperSerummcgmL = new PanelMarker({
      attribute: 'copperSerummcgmL',
      value: this.bloodwork.copperSerummcgmL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.cpeptide = new PanelMarker({
      attribute: 'cpeptide',
      value: this.bloodwork.cpeptide,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.dDimer = new PanelMarker({
      attribute: 'dDimer',
      value: this.bloodwork.dDimer,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.fibrinogen = new PanelMarker({
      attribute: 'fibrinogen',
      value: this.bloodwork.fibrinogen,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.folateRBC = new PanelMarker({
      attribute: 'folateRBC',
      value: this.bloodwork.folateRBC,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.folateSerum = new PanelMarker({
      attribute: 'folateSerum',
      value: this.bloodwork.folateSerum,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.ggt = new PanelMarker({
      attribute: 'ggt',
      value: this.bloodwork.ggt,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.homocysteine = new PanelMarker({
      attribute: 'homocysteine',
      value: this.bloodwork.homocysteine,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.hsCRP = new PanelMarker({
      attribute: 'hsCRP',
      value: this.bloodwork.hsCRP,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.esr = new PanelMarker({
      attribute: 'esr',
      value: this.bloodwork.esr,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.insulin = new PanelMarker({
      attribute: 'insulin',
      value: this.bloodwork.insulin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.lactateDehydrogenase = new PanelMarker({
      attribute: 'lactateDehydrogenase',
      value: this.bloodwork.lactateDehydrogenase,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.magnesiumRBC = new PanelMarker({
      attribute: 'magnesiumRBC',
      value: this.bloodwork.magnesiumRBC,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.magnesium = new PanelMarker({
      attribute: 'magnesium',
      value: this.bloodwork.magnesium,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.phosphorus = new PanelMarker({
      attribute: 'phosphorus',
      value: this.bloodwork.phosphorus,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    //12-10-25 MA
    this.psaTotal = new PanelMarker({
      attribute: 'psaTotal',
      value: this.bloodwork.psaTotal,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.psaFree = new PanelMarker({
      attribute: 'psaFree',
      value: this.bloodwork.psaFree,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.psaFreePercent = new PanelMarker({
      attribute: 'psaFreePercent',
      value: this.bloodwork.psaFreePercent,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.reticulocyteCount = new PanelMarker({
      attribute: 'reticulocyteCount',
      value: this.bloodwork.reticulocyteCount,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.uricAcid = new PanelMarker({
      attribute: 'uricAcid',
      value: this.bloodwork.uricAcid,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.vitaminA = new PanelMarker({
      attribute: 'vitaminA',
      value: this.bloodwork.vitaminA,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.vitaminB6 = new PanelMarker({
      attribute: 'vitaminB6',
      value: this.bloodwork.vitaminB6,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.vitaminB12 = new PanelMarker({
      attribute: 'vitaminB12',
      value: this.bloodwork.vitaminB12,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.vitaminD = new PanelMarker({
      attribute: 'vitaminD',
      value: this.bloodwork.vitaminD,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.vitaminD125 = new PanelMarker({
      attribute: 'vitaminD125',
      value: this.bloodwork.vitaminD125,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.zincRBCμgdL = new PanelMarker({
      attribute: 'zincRBCμgdL',
      value: this.bloodwork.zincRBCμgdL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.zincRBCmgL = new PanelMarker({
      attribute: 'zincRBCmgL',
      value: this.bloodwork.zincRBCmgL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.zincSerumμgdL = new PanelMarker({
      attribute: 'zincSerumμgdL',
      value: this.bloodwork.zincSerumμgdL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.zincSerummcgmL = new PanelMarker({
      attribute: 'zincSerummcgmL',
      value: this.bloodwork.zincSerummcgmL,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.populateCopperZincRatio();
    this.populatePercentOfFreeCopper();
  }
  /**
        Both values must have a number before the formula can be calculated. 
        Zinc, serum must be greater than zero
        Also, the the units have to match up for the formulas to work properly.
        Copper, serum and zinc, serum should both be μg/dL OR both mcg/mL
        If one is in μg/dL and the other is in mcg/mL, multiply the value with the units of mcg/mL by 100.
     */
  populateCopperZincRatio?() {
    let copperZincRatio;
    if (
      this.copperSerummcgmL.hasValue() &&
      this.zincSerummcgmL.isGreaterThanZero()
    ) {
      copperZincRatio = this.copperSerummcgmL.value / this.zincSerummcgmL.value;
    } else if (
      this.copperSerumμgdL.hasValue() &&
      this.zincSerumμgdL.isGreaterThanZero()
    ) {
      copperZincRatio = this.copperSerumμgdL.value / this.zincSerumμgdL.value;
    } else if (
      this.copperSerummcgmL.hasValue() &&
      this.zincSerumμgdL.isGreaterThanZero()
    ) {
      copperZincRatio =
        (this.copperSerummcgmL.value * 100) / this.zincSerumμgdL.value;
    } else if (
      this.copperSerumμgdL.hasValue() &&
      this.zincSerummcgmL.isGreaterThanZero()
    ) {
      copperZincRatio =
        (this.copperSerumμgdL.value / this.zincSerummcgmL.value) * 100;
    }

    this.copperZincRatio = new PanelMarker({
      attribute: 'copperZincRatio',
      value: copperZincRatio,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  /**
        % of Free Copper = ((copper, serum - ceruloplasmin x 0.3%) / copper, serum ) x 100
        ----- NOTE: ceruloplasmin contains 0.3% copper
        ----- Copper and ceruloplasmin have to be in the same units so there are two formulas for each group of units we use for copper.
        1) If copper is in (μg/dL, mcg/dL) and ceruloplasmin is always in mg/dL the formula is:
        ----- ((copper, serum - ceruloplasmin x 3 ) / copper, serum) x 100
        ----- 1 μg/dL = 0.001 mg/dL (1 mg/dL = 1000 μg/dL)
        ----- For example, if copper = 90 μg/dL and ceruloplasmin = 16.1 mg/dL
        ----- ((90 μg/dL - 16.1 mg/dL x 3)/ 90 μg/dL) x 100 = 46.33 %

        2) If copper is in (mcg/mL, μg/mL, mg/L) and ceruloplasmin is always in mg/dL the formula is:
        ----- ((copper, serum - ceruloplasmin x 0.03 ) / copper, serum) x 100
        ----- 1 μg/mL = 0.1 mg/dL; 1 mg/dL = 10 μg/mL
        ----- For example, if copper = 0.90 mcg/mL and ceruloplasmin = 16.1 mg/dL
        ----- ((0.90 μg/dL - 16.1 mg/dL x 0.03)/ 0.90 μg/dL) x 100 = 46.33 %
     */
  populatePercentOfFreeCopper?() {
    let percentOfFreeCopper;
    if (
      this.ceruloplasmin.hasValue() &&
      this.copperSerumμgdL.isGreaterThanZero()
    ) {
      percentOfFreeCopper =
        ((this.copperSerumμgdL.value - 3 * this.ceruloplasmin.value) /
          this.copperSerumμgdL.value) *
        100;
    } else if (
      this.ceruloplasmin.hasValue() &&
      this.copperSerummcgmL.isGreaterThanZero()
    ) {
      percentOfFreeCopper =
        ((this.copperSerummcgmL.value - 0.03 * this.ceruloplasmin.value) /
          this.copperSerummcgmL.value) *
        100;
    }

    this.percentOfFreeCopper = new PanelMarker({
      attribute: 'percentOfFreeCopper',
      value: percentOfFreeCopper,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  populateThyroidPanelMarkers?() {
    this.tsh = new PanelMarker({
      attribute: 'tsh',
      value: this.bloodwork.tsh,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.t4Total = new PanelMarker({
      attribute: 't4Total',
      value: this.bloodwork.t4Total,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.t4Free = new PanelMarker({
      attribute: 't4Free',
      value: this.bloodwork.t4Free,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.t3Total = new PanelMarker({
      attribute: 't3Total',
      value: this.bloodwork.t3Total,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.t3Free = new PanelMarker({
      attribute: 't3Free',
      value: this.bloodwork.t3Free,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.reverseT3 = new PanelMarker({
      attribute: 'reverseT3',
      value: this.bloodwork.reverseT3,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.tpoAb = new PanelMarker({
      attribute: 'tpoAb',
      value: this.bloodwork.tpoAb,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.tgAb = new PanelMarker({
      attribute: 'tgAb',
      value: this.bloodwork.tgAb,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.tbg = new PanelMarker({
      attribute: 'tbg',
      value: this.bloodwork.tbg,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.t3Uptake = new PanelMarker({
      attribute: 't3Uptake',
      value: this.bloodwork.t3Uptake,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.freeT4Index = new PanelMarker({
      attribute: 'freeT4Index',
      value: this.bloodwork.freeT4Index,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  populateSexHormonePanelMarkers?() {
    const menstrualPhase = this.bloodwork.menstrualPhase
      ? this.bloodwork.menstrualPhase
      : '';

    this.menstrualPhase = new PanelMarker({
      attribute: 'menstrualPhase',
      value: undefined,
      altValue: menstrualPhase,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.antiMullerianHormone = new PanelMarker({
      attribute: 'antiMullerianHormone',
      value: this.bloodwork.antiMullerianHormone,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.cortisolAM = new PanelMarker({
      attribute: 'cortisolAM',
      value: this.bloodwork.cortisolAM,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.cortisolPM = new PanelMarker({
      attribute: 'cortisolPM',
      value: this.bloodwork.cortisolPM,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.dheaS = new PanelMarker({
      attribute: 'dheaS',
      value: this.bloodwork.dheaS,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.dihydrotestosterone = new PanelMarker({
      attribute: 'dihydrotestosterone',
      value: this.bloodwork.dihydrotestosterone,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    const estradiolAttribute = 'estradiolE2';
    this.estradiolE2 = new PanelMarker({
      attribute: menstrualPhase
        ? `${estradiolAttribute}${menstrualPhase}`
        : estradiolAttribute,
      value: this.bloodwork.estradiolE2,
    }).init(
      this.bloodworkRanges,
      this.bloodworkFields,
      this.bloodwork,
      estradiolAttribute,
    );

    this.estriolE3 = new PanelMarker({
      attribute: 'estriolE3',
      value: this.bloodwork.estriolE3,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    const estrogensTotalAttribute = 'estrogensTotal';
    this.estrogensTotal = new PanelMarker({
      attribute: menstrualPhase
        ? `${estrogensTotalAttribute}${menstrualPhase}`
        : estrogensTotalAttribute,
      value: this.bloodwork.estrogensTotal,
    }).init(
      this.bloodworkRanges,
      this.bloodworkFields,
      this.bloodwork,
      estrogensTotalAttribute,
    );

    this.estroneE1 = new PanelMarker({
      attribute: 'estroneE1',
      value: this.bloodwork.estroneE1,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    const fshAttribute = 'fsh';
    this.fsh = new PanelMarker({
      attribute: menstrualPhase
        ? `${fshAttribute}${menstrualPhase}`
        : fshAttribute,
      value: this.bloodwork.fsh,
    }).init(
      this.bloodworkRanges,
      this.bloodworkFields,
      this.bloodwork,
      fshAttribute,
    );

    this.hCG = new PanelMarker({
      attribute: 'hCG',
      value: this.bloodwork.hCG,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.igf1 = new PanelMarker({
      attribute: 'igf1',
      value: this.bloodwork.igf1,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    const lhAttribute = 'lh';
    this.lh = new PanelMarker({
      attribute: menstrualPhase
        ? `${lhAttribute}${menstrualPhase}`
        : lhAttribute,
      value: this.bloodwork.lh,
    }).init(
      this.bloodworkRanges,
      this.bloodworkFields,
      this.bloodwork,
      lhAttribute,
    );

    this.pregnenolone = new PanelMarker({
      attribute: 'pregnenolone',
      value: this.bloodwork.pregnenolone,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    const progesteroneAttribute = 'progesterone';
    this.progesterone = new PanelMarker({
      attribute: menstrualPhase
        ? `${progesteroneAttribute}${menstrualPhase}`
        : progesteroneAttribute,
      value: this.bloodwork.progesterone,
    }).init(
      this.bloodworkRanges,
      this.bloodworkFields,
      this.bloodwork,
      progesteroneAttribute,
    );

    const hydroxyprogesterone17Attribute = 'hydroxyprogesterone17';
    this.hydroxyprogesterone17 = new PanelMarker({
      attribute: menstrualPhase
        ? `${hydroxyprogesterone17Attribute}${menstrualPhase}`
        : hydroxyprogesterone17Attribute,
      value: this.bloodwork.hydroxyprogesterone17,
    }).init(
      this.bloodworkRanges,
      this.bloodworkFields,
      this.bloodwork,
      hydroxyprogesterone17Attribute,
    );

    this.prolactin = new PanelMarker({
      attribute: 'prolactin',
      value: this.bloodwork.prolactin,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.prolactinLactating = new PanelMarker({
      attribute: 'prolactinLactating',
      value: this.bloodwork.prolactinLactating,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.shbg = new PanelMarker({
      attribute: 'shbg',
      value: this.bloodwork.shbg,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.testosteroneBioavailable = new PanelMarker({
      attribute: 'testosteroneBioavailable',
      value: this.bloodwork.testosteroneBioavailable,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.testosteroneBioavailablePercent = new PanelMarker({
      attribute: 'testosteroneBioavailablePercent',
      value: this.bloodwork.testosteroneBioavailablePercent,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.testosteroneFreeCalc = new PanelMarker({
      attribute: 'testosteroneFreeCalc',
      value: this.bloodwork.testosteroneFreeCalc,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.testosteroneFreeDirect = new PanelMarker({
      attribute: 'testosteroneFreeDirect',
      value: this.bloodwork.testosteroneFreeDirect,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.testosteroneTotal = new PanelMarker({
      attribute: 'testosteroneTotal',
      value: this.bloodwork.testosteroneTotal,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);

    this.testosteroneTotalLCMS = new PanelMarker({
      attribute: 'testosteroneTotalLCMS',
      value: this.bloodwork.testosteroneTotalLCMS,
    }).init(this.bloodworkRanges, this.bloodworkFields, this.bloodwork);
  }

  calculateAGAG?(anionGap: number): number {
    if (anionGap == null || !this.albumin.hasValue()) {
      return undefined;
    }

    return anionGap + 2.5 * (4.4 - this.albumin.value);
  }

  hasAnionGapValues?(): boolean {
    return (
      this.sodium.hasValue() &&
      this.chloride.hasValue() &&
      this.carbonDioxide.hasValue()
    );
  }

  performAnalysis?() {
    this.reset();

    if (this.isAnemiaAnalyisPossible()) {
      this.calculateAnemicProbability();
      this.calculateAnemicProbabilityCategory();
      this.calculateDehydrationProbabilty();
      this.calculateAcuteDehydrationProbabilty();
      this.calculateChronicDehydrationProbabilty();
      this.calculatePossibleRootCausesForDehydration();
      this.calculatePoorOxygenationCategory();
      this.calculatePoorOxgenationCategoryDueToSmoking();
    }

    if (this.isRedBloodCellSizeAnalysisPossible()) {
      this.calculateRedBloodCellsSize();
    }

    if (this.isRedBloodCellColorAnalysisPossible()) {
      this.calculateRedBloodCellsColor();
    }

    this.boneMarrowAnalysis.performAnalysis();
    this.electrolytesAnalysis.performAnalysis();
    this.glucoseAnalysis.performAnalysis();
    this.hormonesAnalysis.performAnalysis();
    this.inflammationOxidativeStressAnalysis.performAnalysis();
    this.ironAnalysis.performAnalysis();
    this.kidneyAnalysis.performAnalysis();
    this.lipidsAnalysis.performAnalysis();
    this.liverBiliaryAnalysis.performAnalysis();
    this.nutrientsAnalysis.performAnalysis();
    this.plateletsAnalysis.performAnalysis();
    this.proteinsAnalysis.performAnalysis();
    this.thyroidAnalysis.performAnalysis();
    this.wbcAnalysis.performAnalysis();
    this.prostateAnalysis.performAnalysis(); //12-10-25 MA

    this.populateNutrientDeficiencyOrInflammationLikelyText();
    this.calculatePossibleRootCauses();

    if (this.boneMarrowAnalysis.boneMarrowOverproductionIsLikely) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_150,
      );
    }

    // Sort possible root cause categories because some rules maybe calculated out of order so that
    // the logic is simpler.
    this.possibleRootCauseCategories = PossibleRootCauseCategory.sort(
      this.possibleRootCauseCategories,
    );
  }

  calculatePossibleRootCausesForDehydration?() {
    if (this.isChronicDehydrationPossibleOrHigher()) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_1200,
      );
    } else if (this.isAcuteDehydrationPossibleOrHigher()) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_1100,
      );
    } else if (this.isDehydrationPossibleOrHigher()) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_1000,
      );
    }
  }

  isAnemiaAnalyisPossible?(): boolean {
    return this.rbc.hasValue() && this.hgb.hasValue() && this.hct.hasValue();
  }

  isRedBloodCellSizeAnalysisPossible?(): boolean {
    return this.mcv.hasValue();
  }

  isRedBloodCellColorAnalysisPossible?(): boolean {
    return this.mcv.hasValue() || this.mchc.hasValue();
  }

  isAnyDehydrationPossibleOrHigher?(): boolean {
    return (
      this.isChronicDehydrationPossibleOrHigher() ||
      this.isAcuteDehydrationPossibleOrHigher() ||
      this.isDehydrationPossibleOrHigher()
    );
  }

  deriveDehydrationText?(): string {
    if (this.isChronicDehydrationPossibleOrHigher()) {
      return `Chronic Dehydration is ${this.chronicDehydrationProbabilityCategory}`;
    } else if (this.isAcuteDehydrationPossibleOrHigher()) {
      return `Acute Dehydration is ${this.acuteDehydrationProbabilityCategory}`;
    } else if (this.isDehydrationPossibleOrHigher()) {
      return `Dehydration is ${this.dehydrationProbabilityCategory}`;
    }
    return '';
  }

  derivePoorOxygenationText?(): string {
    if (this.isPoorOxygenationPossibleOrHigher()) {
      return `Oxygen Insufficiency is ${this.poorOxygenationProbabilityCategory}`;
    }
    return '';
  }

  derivePoorOxygenationTextForSmoker?(): string {
    if (this.isPoorOxygenationDueToSmoking()) {
      return 'Inhaled Smoke is Likely Contributing to Oxygen Insufficiency';
    }
    return '';
  }

  calculateRedBloodCellsSize?() {
    console.log('calculating rbcSize...');
    // Normocytic (Normal) MCV is WITHIN the optimal range
    if (this.mcv.isOptimal()) {
      this.redBloodCellSize = RedBloodCellSize.NORMAL;
      // Microcytic (Small) MCV is BELOW the minimum optimal range
    } else if (this.mcv.isStricklyLessThanOptimal()) {
      this.redBloodCellSize = RedBloodCellSize.SMALL;
      // Macrocytic (Large) MCV is ABOVE the maximum optimal range
    } else if (this.mcv.isGreaterThanOptimal()) {
      this.redBloodCellSize = RedBloodCellSize.LARGE;
      // N/A no value for MCV
    } else {
      this.redBloodCellSize = RedBloodCellSize.NOT_APPLICABLE;
    }
  }

  calculateRedBloodCellsColor?() {
    console.log('calculating rbcColor...');
    // IF MCHC = Opt THEN Normochromic (Normal)
    // IF MCHC < Opt THEN Hypochromic (Pale)
    // ELSE Hyperchromic (Dark)
    if (this.mchc.isOptimal()) {
      this.redBloodCellColor = RedBloodCellColor.NORMAL;
    } else if (this.mchc.isStricklyLessThanOptimal()) {
      this.redBloodCellColor = RedBloodCellColor.PALE;
    } else {
      this.redBloodCellColor = RedBloodCellColor.DARK;
    }

    console.log('redBloodCellColor: ' + JSON.stringify(this.redBloodCellColor));
  }

populateNutrientDeficiencyOrInflammationLikelyText?() {
  if (this.isAnemiaProbabilityPossibleOrHigher()) {
    if (this.rdw.isGreaterThanOptimal()) {
      if (
        this.ferritin.isGreaterThanOptimal() &&
        this.hsCRP.isGreaterThanOptimal()
      ) {
        this.nutrientDeficiencyOrInflammationLikelyText =
          RootCause.INFLAMMATION_LIKELY;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_1761,
        );
      } else if (
        this.hsCRP.isGreaterThanOptimal() &&
        this.ferritin.isEmptyOrIsLessThanOrIsOptimal()
      ) {
        this.nutrientDeficiencyOrInflammationLikelyText =
          RootCause.INFLAMMATION_LIKELY;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_1762,
        );
      } else if (
        this.ferritin.isEmptyOrIsLessThanOrIsOptimal() &&
        this.hsCRP.isEmptyOrIsLessThanOrIsOptimal()
      ) {
        this.nutrientDeficiencyOrInflammationLikelyText =
          RootCause.NUTRIENT_INSUFFICIENCY;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_1755,
        );
      } else if (
        this.ferritin.isGreaterThanOptimal() &&
        this.hsCRP.isEmptyOrIsLessThanOrIsOptimal()
      ) {
        this.nutrientDeficiencyOrInflammationLikelyText =
          RootCause.INFLAMMATION_LIKELY;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_1760,
        );
      }
    }
  } else if (
    this.anemicProbabilityCategory === ProbabilityCategory.LESS_LIKELY
  ) {
    if (this.rdw.isGreaterThanOptimal()) {
      this.nutrientDeficiencyOrInflammationLikelyText =
        RootCause.INFLAMMATION_LIKELY;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_1765,
      );
    }
  }
}

  getRBCPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.rbc,
      this.hgb,
      this.hct,
      this.mcv,
      this.mch,
      this.mchc,
      this.rdw,
      this.reticulocyteCount,
      this.rpi,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getIronPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.iron,
      this.tibc,
      this.saturation,
      this.ferritin,
      this.transferrin,
      this.ceruloplasmin,
      this.copperRBCμgmL,
      this.copperRBCμgdL,
      this.copperSerumμgdL,
      this.copperSerummcgmL,
      this.percentOfFreeCopper,
      this.vitaminA,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getWBCPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.wbc,
      this.neutrophils,
      this.lymphocytes,
      this.monocytes,
      this.eosinophils,
      this.basophils,
      this.neutrophilsAbs,
      this.lymphocytesAbs,
      this.monocytesAbs,
      this.eosinophilsAbs,
      this.basophilsAbs,
      this.nlr,
      this.globulin,
      this.alp,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getPlateletPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.platelets,
      this.mpv,
      this.dDimer,
      this.fibrinogen,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getGlucosePanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.glucose,
      this.hemoglobinA1c,
      this.insulin,
      this.cpeptide,
      this.triglyceridesHDLRatio,
      this.triglycerides,
      this.hdl,  
      this.amylase,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getKidneyPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.bun,
      this.creatinine,
      this.eGFR,
      this.bunCreatinineRatio,
      this.uricAcid,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  // Nutrients
  getMineralPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.calcium,
      this.ceruloplasmin,
      this.copperRBCμgmL,
      this.copperRBCμgdL,
      this.copperSerumμgdL,
      this.copperSerummcgmL,
      this.copperZincRatio,
      this.percentOfFreeCopper,
      this.folateRBC,
      this.folateSerum,
      this.magnesiumRBC,
      this.magnesium,
      this.phosphorus,
      this.vitaminA,
      this.vitaminB6,
      this.vitaminB12,
      this.vitaminD,
      this.vitaminD125,
      this.zincRBCμgdL,
      this.zincRBCmgL,
      this.zincSerumμgdL,
      this.zincSerummcgmL,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getElectrolytePanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.sodium,
      this.potassium,
      this.sodiumPotassiumRatio,
      this.chloride,
      this.carbonDioxide,
      this.anionGapWithoutK,
      this.anionGap,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getProteinsPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.protein,
      this.albumin,
      this.globulin,
      this.albuminGlobulinRatio,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getLiverPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.bileAcids,
      this.bilirubin,
      this.bilirubinDirect,
      this.bilirubinIndirect,
      this.alp,
      this.ast,
      this.alt,
      this.ggt,
      this.lactateDehydrogenase,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getLipidsPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.cholesterol,
      this.triglycerides,
      this.hdl,
      this.ldl,
      this.cholesterolHDLRatio,
      this.triglyceridesHDLRatio,
      this.apolipoproteinA1,
      this.apolipoproteinB,
      this.apoBapoA1Ratio,
      this.lipoproteinA,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getInflammationOxidativeStressPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.albumin,
      this.bilirubin,
      this.ceruloplasmin,
      this.ferritin,
      this.fibrinogen,
      this.ggt,
      this.hdl,
      this.homocysteine,
      this.hsCRP,
      this.esr,
      this.tibc,
      this.transferrin,
      this.uricAcid,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getThyroidPanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.tsh,
      this.t4Total,
      this.t4Free,
      this.t3Total,
      this.t3Free,
      this.reverseT3,
      this.tpoAb,
      this.tgAb,
      this.tbg,
      this.t3Uptake,
      this.freeT4Index,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getSexHormonePanelMarkers?(): PanelMarker[] {
    const panelMarkers = [
      this.antiMullerianHormone,
      this.cortisolAM,
      this.cortisolPM,
      this.dheaS,
      this.dihydrotestosterone,
      this.estradiolE2,
      this.estriolE3,
      this.estrogensTotal,
      this.estroneE1,
      this.fsh,
      this.hCG,
      this.igf1,
      this.lh,
      this.pregnenolone,
      this.progesterone,
      this.hydroxyprogesterone17,
      this.prolactin,
      this.prolactinLactating,
      this.shbg,
      this.testosteroneBioavailable,
      this.testosteroneBioavailablePercent,
      this.testosteroneFreeCalc,
      this.testosteroneFreeDirect,
      this.testosteroneTotal,
      this.testosteroneTotalLCMS,
    ];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  getProstatePanelMarkers?(): PanelMarker[] {
    const panelMarkers = [this.psaTotal, this.psaFree, this.psaFreePercent];
    return panelMarkers.filter(panelMarker => {
      return panelMarker.hasValue();
    });
  }

  possibleRootCauses?(): RootCause[] {
    const possibleRootCauses = [];
    this.possibleRootCauseCategories.forEach(cat => {
      possibleRootCauses.push(...cat.possibleRootCauses);
    });
    return possibleRootCauses;
  }

  possibleRootCausesWBC?(): RootCause[] {
    const possibleRootCauses = [];
    this.wbcAnalysis.possibleRootCauseCategories.forEach(cat => {
      possibleRootCauses.push(...cat.possibleRootCauses);
    });
    return possibleRootCauses;
  }

  possibleRootCauseCategoriesFor?(
    section: Section | string,
  ): PossibleRootCauseCategory[] {
    switch (section) {
      case Section.WBC: // aka Immune System Analysis
        return this.wbcAnalysis.possibleRootCauseCategories;
      case Section.IRON:
        return this.ironAnalysis.possibleRootCauseCategories;
      case Section.PLATELETS: // aka Coagulation Analysis
        return this.plateletsAnalysis.possibleRootCauseCategories;
      case Section.GLUCOSE:
        return this.glucoseAnalysis.possibleRootCauseCategories;
      case Section.KIDNEY:
        return this.kidneyAnalysis.possibleRootCauseCategories;
      case Section.NUTRIENTS:
        return this.nutrientsAnalysis.possibleRootCauseCategories;
      case Section.ELECTROLYTES:
        return this.electrolytesAnalysis.possibleRootCauseCategories;
      case Section.PROTEINS:
        return this.proteinsAnalysis.possibleRootCauseCategories;
      case Section.LIVER_BILIARY:
        return this.liverBiliaryAnalysis.possibleRootCauseCategories;
      case Section.LIPIDS:
        return this.lipidsAnalysis.possibleRootCauseCategories;
      case Section.INFLAMMATION_OXIDATIVE_STRESS:
        return this.inflammationOxidativeStressAnalysis
          .possibleRootCauseCategories;
      case Section.THYROID:
        return this.thyroidAnalysis.possibleRootCauseCategories;
      case Section.SEX_HORMONE:
        return this.hormonesAnalysis.possibleRootCauseCategories;
      case Section.PROSTATE:
        return this.prostateAnalysis.possibleRootCauseCategories;
      default:
        return this.possibleRootCauseCategories;
    }
  }

  isChronicDehydrationPossibleOrHigher?(): boolean {
    return (
      this.chronicDehydrationProbabilityCategory !== ProbabilityCategory.NA &&
      this.chronicDehydrationProbabilityCategory !==
        ProbabilityCategory.LESS_LIKELY
    );
  }

  isAcuteDehydrationPossibleOrHigher?(): boolean {
    return (
      this.acuteDehydrationProbabilityCategory !== ProbabilityCategory.NA &&
      this.acuteDehydrationProbabilityCategory !==
        ProbabilityCategory.LESS_LIKELY
    );
  }

  isDehydrationPossibleOrHigher?(): boolean {
    return (
      this.dehydrationProbabilityCategory !== ProbabilityCategory.NA &&
      this.dehydrationProbabilityCategory !== ProbabilityCategory.LESS_LIKELY
    );
  }

  isPoorOxygenationPossibleOrHigher?(): boolean {
    return this.poorOxygenationProbabilityCategory !== ProbabilityCategory.NA;
  }

  isPoorOxygenationDueToSmoking?(): boolean {
    return PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_1400,
    );
  }

  private calculateAnemicProbability?() {
    this.anemiaHighlyLikelyProbability();
    this.anemiaLikelyProbability();
    this.anemiaPossibleProbability();
    this.anemiaLessLikelyProbability();
  }

  private calculateAnemicProbabilityCategory?() {
    if (this.anemicProbability >= 90) {
      // 90 - 100
      this.anemicProbabilityCategory = ProbabilityCategory.HIGHLY_LIKELY;
    } else if (this.anemicProbability >= 70) {
      // 70 - 89
      this.anemicProbabilityCategory = ProbabilityCategory.LIKELY;
    } else if (this.anemicProbability >= 50) {
      // 50 - 69
      this.anemicProbabilityCategory = ProbabilityCategory.POSSIBLE;
    } else if (this.anemicProbability >= 0) {
      // 0 - 49
      this.anemicProbabilityCategory = ProbabilityCategory.LESS_LIKELY;
    }
  }

  private anemiaHighlyLikelyProbability?() {
    // Highly Likely 100%
    // RBC < Lab AND HGB < Lab AND HCT < Lab
    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isLessThanLab() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 99;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2000,
      );
      return;
    }
    // Highly Likely 99%
    // RBC < Lab AND HGB < Lab AND HCT < Opt
    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isLessThanLab() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 99;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2010,
      );
      return;
    }
    // Highly Likely 98%
    // RBC < Opt AND HGB < Lab AND HCT < Lab
    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isLessThanLab() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 98;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2020,
      );
      return;
    }
    // Highly Likely 97%
    // RBC < Lab AND HGB < Opt AND HCT < Lab
    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isLessThanOptimal() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 97;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2030,
      );
      return;
    }
    // Highly Likely 96%
    // RBC < Lab AND HGB < Lab AND HCT >= Opt
    // RBC >= Opt AND HGB < Lab AND HCT < Lab
    // RBC < Lab AND HGB >= Opt AND HCT < Lab
    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isLessThanLab() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 96;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2040,
      );
      return;
    }

    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isLessThanLab() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 96;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2050,
      );
      return;
    }

    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 96;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2060,
      );
      return;
    }

    // Highly Likely 95%
    // RBC < Opt AND HGB < Lab AND HCT < Opt
    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isLessThanLab() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 95;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2070,
      );
      return;
    }
    // Highly Likely 94%
    // RBC < Lab AND HGB < Opt AND HCT < Opt
    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isLessThanOptimal() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 94;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2080,
      );
      return;
    }
    // Highly Likely 93%
    // RBC < Opt AND HGB < Opt AND HCT < Lab
    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isLessThanOptimal() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 93;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2090,
      );
      return;
    }
    // Highly Likely 92%
    // (RBC < Opt AND HGB < Lab AND HCT >= Opt) OR
    // (RBC < Lab AND HGB < Opt AND HCT >=Opt) OR
    // If all 3 of RBC, HGB, and HCT have a value in the range below:
    // < (Opt Min + Lab Min)/2 AND >= Lab Min
    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isLessThanLab() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 92;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2100,
      );
      return;
    }

    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isLessThanOptimal() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 92;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2110,
      );
      return;
    }

    if (
      this.lowEndOptLabMin(this.rbc) &&
      this.lowEndOptLabMin(this.hgb) &&
      this.lowEndOptLabMin(this.hct)
    ) {
      this.anemicProbability = 92;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2120,
      );
      return;
    }

    // Highly Likely 91%
    // (RBC >= Opt AND HGB < Lab AND HCT < Opt) OR
    // (RBC < Lab AND HGB >= Opt AND HCT < Opt) OR
    // anemicProbability91Rule
    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isLessThanLab() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 91;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2130,
      );
      return;
    }

    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 91;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2140,
      );
      return;
    }

    if (this.anemicProbability91Rule()) {
      this.anemicProbability = 91;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2150,
      );
      return;
    }

    // Highly Likely 90%
    // RBC >= Opt AND HGB < Opt AND HCT < Lab OR
    // RBC < Opt AND HGB >= Opt AND HCT < Lab OR
    // RBC < Opt AND HGB < Opt AND HCT < Opt
    if (
      (this.rbc.isGreaterThanOrIsOptimal() &&
        this.hgb.isLessThanOptimal() &&
        this.hct.isLessThanLab()) ||
      (this.rbc.isLessThanOptimal() &&
        this.hgb.isGreaterThanOrIsOptimal() &&
        this.hct.isLessThanLab()) ||
      this.anemicProbability90Rule()
    ) {
      this.anemicProbability = 90;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2160,
      );
      return;
    }

    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 90;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2170,
      );
      return;
    }

    if (this.anemicProbability90Rule()) {
      this.anemicProbability = 90;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2180,
      );
      return;
    }
  }

  private lowEndOptLabMin?(pm: PanelMarker): number {
    return pm.isLessThanAvgMin() && pm.isGreaterThanOrIsLab() ? 1 : 0;
  }

  private highEndOptLabMin?(pm: PanelMarker): number {
    return pm.isStricklyLessThanOptimal() && pm.isGreaterThanOrEqualToAvgMin()
      ? 1
      : 0;
  }

  private anemicProbability91Rule?(): boolean {
    // 2 parts to this rule:
    // 1.)  If 2 of RBC, HGB, and HCT have a value in the range below:
    // < (Opt Min + Lab Min)/2 AND >= Lab Min
    // AND
    // 2.)  If 1 of RBC, HGB, or HCT have a value in the range below:
    // < Opt Min AND >= (Opt Min + Lab Min)/2"
    const lowEndOptLabMinCount =
      this.lowEndOptLabMin(this.rbc) +
      this.lowEndOptLabMin(this.hgb) +
      this.lowEndOptLabMin(this.hct);
    const highEndOptLabMinCount =
      this.highEndOptLabMin(this.rbc) +
      this.highEndOptLabMin(this.hgb) +
      this.highEndOptLabMin(this.hct);
    return lowEndOptLabMinCount === 2 && highEndOptLabMinCount === 1;
  }

  private anemicProbability90Rule?(): boolean {
    // IF TWO of RBC, HGB, AND HCT are (< Opt Min AND >= (Opt Min + Lab Min)/2) AND
    // ONE of RBC, HGB, AND HCT are < (Opt Min + Lab Min)/2
    const highEndOptLabMinCount =
      this.highEndOptLabMin(this.rbc) +
      this.highEndOptLabMin(this.hgb) +
      this.highEndOptLabMin(this.hct);

    let lessThanAvgMinCount = this.rbc.isLessThanAvgMin() ? 1 : 0;
    lessThanAvgMinCount =
      lessThanAvgMinCount + (this.hgb.isLessThanAvgMin() ? 1 : 0);
    lessThanAvgMinCount =
      lessThanAvgMinCount + (this.hct.isLessThanAvgMin() ? 1 : 0);

    return highEndOptLabMinCount === 2 && lessThanAvgMinCount === 1;
  }

  private anemiaLikelyProbability?() {
    if (this.anemicProbability > -1) {
      return;
    }
    // Likely 89%
    // RBC >= Opt AND HGB < Lab AND HCT >= Opt
    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isLessThanLab() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 89;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2190,
      );
      return;
    }

    // Likely 88%
    // [RBC < Opt Min AND >= (Opt Min + Lab Min)/2] AND
    // [HGB < Opt Min AND >= (Opt Min + Lab Min)/2] AND
    // [HCT < Opt Min AND >= (Opt Min + Lab Min)/2]
    if (
      this.highEndOptLabMin(this.rbc) &&
      this.highEndOptLabMin(this.hgb) &&
      this.highEndOptLabMin(this.hct)
    ) {
      this.anemicProbability = 88;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2200,
      );
      return;
    }

    // Likely 87%
    // RBC < Lab AND HGB >= Opt AND HCT >= Opt
    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 87;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2210,
      );
      return;
    }
    // Likely 86%
    // RBC < Opt AND HGB < Opt AND HCT >= Opt
    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isLessThanOptimal() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 86;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2220,
      );
      return;
    }
    // Likely 85%
    // RBC >= Opt AND HGB < Opt AND HCT < Opt
    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isLessThanOptimal() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 85;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2230,
      );
      return;
    }
    // Likely 84%
    // RBC < Opt AND HGB >= Opt AND HCT < Opt
    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 84;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2240,
      );
      return;
    }
  }

  private anemiaPossibleProbability?() {
    if (this.anemicProbability > -1) {
      return;
    }
    // Possible 83%
    // RBC >= Opt AND HGB >= Opt AND HCT < Lab
    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isLessThanLab()
    ) {
      this.anemicProbability = 83;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2250,
      );
      return;
    }
    // Possible 82%
    // RBC >= Opt AND HGB < (Opt Min + Lab Min)/2 AND HCT >= Opt
    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isLessThanAvgMin() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 82;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2260,
      );
      return;
    }
    // Possible 81%
    // RBC < Opt AND HGB >= Opt AND HCT >= Opt
    if (
      this.rbc.isLessThanOptimal() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 81;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2270,
      );
      return;
    }
    // Possible 80%
    // RBC >= Opt AND HGB >= Opt AND HCT < Opt
    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isGreaterThanOrIsOptimal() &&
      this.hct.isLessThanOptimal()
    ) {
      this.anemicProbability = 80;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2280,
      );
      return;
    }
    // Possible 79%
    // RBC AND HGB AND HCT >= Opt OR >= Lab AND
    // (MCV < Lab OR MCV > Lab) AND RDW > Lab
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isLessThanLab() &&
      this.rdw.isGreaterThanLab()
    ) {
      this.anemicProbability = 79;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2290,
      );
      return;
    }

    if (
      this.commonAnemiaConditions() &&
      this.mcv.isGreaterThanLab() &&
      this.rdw.isGreaterThanLab()
    ) {
      this.anemicProbability = 79;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2300,
      );
      return;
    }

    // Possible 76%
    // RBC >= Opt AND [HGB < Opt Min AND >= (Opt Min + Lab Min)/2] AND HCT >= Opt
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isLessThanLab() &&
      this.rdw.isGreaterThanOptimal()
    ) {
      this.anemicProbability = 76;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2310,
      );
      return;
    }

    if (
      this.commonAnemiaConditions() &&
      this.mcv.isGreaterThanLab() &&
      this.rdw.isGreaterThanOptimal()
    ) {
      this.anemicProbability = 76;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2320,
      );
      return;
    }

    // Possible 73%
    // RBC AND HGB AND HCT >= Opt OR >= Lab AND
    // MCV < Opt AND RDW > Lab
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isLessThanLab() &&
      this.rdw.isOptimal()
    ) {
      this.anemicProbability = 73;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2330,
      );
      return;
    }

    if (
      this.commonAnemiaConditions() &&
      this.mcv.isGreaterThanLab() &&
      this.rdw.isOptimal()
    ) {
      this.anemicProbability = 73;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2340,
      );
      return;
    }

    // Possible 70%
    // RBC AND HGB AND HCT >= Opt OR >= Lab
    // AND MCV > Opt AND RDW > Lab
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isLessThanOptimal() &&
      this.rdw.isGreaterThanLab()
    ) {
      this.anemicProbability = 70;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2350,
      );
      return;
    }

    if (
      this.commonAnemiaConditions() &&
      this.mcv.isGreaterThanOptimal() &&
      this.rdw.isGreaterThanLab()
    ) {
      this.anemicProbability = 70;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2360,
      );
      return;
    }

    // Possible 65%
    // RBC AND HGB AND HCT >= Opt OR >= Lab
    // AND MCV > Opt AND RDW > Opt
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isLessThanOptimal() &&
      this.rdw.isGreaterThanOptimal()
    ) {
      this.anemicProbability = 65;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2370,
      );
      return;
    }

    if (
      this.commonAnemiaConditions() &&
      this.mcv.isGreaterThanOptimal() &&
      this.rdw.isGreaterThanOptimal()
    ) {
      this.anemicProbability = 65;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2380,
      );
      return;
    }

    // Possible 60%
    // RBC AND HGB AND HCT >= Opt OR >= Lab AND
    // (MVC < Opt OR MCV > Opt) AND RDW = Opt
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isLessThanOptimal() &&
      this.rdw.isOptimal()
    ) {
      this.anemicProbability = 60;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2390,
      );
      return;
    }

    if (
      this.commonAnemiaConditions() &&
      this.mcv.isGreaterThanOptimal() &&
      this.rdw.isOptimal()
    ) {
      this.anemicProbability = 60;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2400,
      );
      return;
    }

    // Possible 56%
    // RBC >= Opt AND (HGB < Opt AND HGB >= AvgMin) AND
    // HCT >= Opt
    if (
      this.rbc.isGreaterThanOrIsOptimal() &&
      this.hgb.isLessThanOptimal() &&
      this.hgb.isGreaterThanOrEqualToAvgMin() &&
      this.hct.isGreaterThanOrIsOptimal()
    ) {
      this.anemicProbability = 56;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2410,
      );
      return;
    }

    // RBC AND HGB AND HCT >= Opt OR >= Lab AND MCV > Opt AND RDW = Opt AND
    if (
      (this.rbc.isGreaterThanOrIsOptimal() ||
        this.rbc.isGreaterThanOrIsLab()) &&
      (this.hgb.isGreaterThanOrIsOptimal() ||
        this.hgb.isGreaterThanOrIsLab()) &&
      (this.hct.isGreaterThanOrIsOptimal() || this.hct.isGreaterThanOrIsLab())
    ) {
      // Possible 55%
      // MCHC < Lab
      if (this.mchc.isLessThanLab()) {
        this.anemicProbability = 55;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_2420,
        );
        return;
      }
      // Possible 50%
      // MCHC < Opt
      if (this.mchc.isLessThanOptimal()) {
        this.anemicProbability = 50;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_2430,
        );
        return;
      }
    }
  }

  private anemiaLessLikelyProbability?() {
    if (this.anemicProbability > -1) {
      return;
    }

    // Less Likely 45%
    // RBC AND HGB AND HCT >= Opt OR >= Lab AND
    // MCV = Opt AND RDW > Lab
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isOptimal() &&
      this.rdw.isGreaterThanLab()
    ) {
      this.anemicProbability = 45;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2440,
      );
      return;
    }
    // Less Likely 30%
    // RBC AND HGB AND HCT >= Opt OR >= Lab AND
    // MCV = Opt AND RDW > Opt
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isOptimal() &&
      this.rdw.isGreaterThanOptimal()
    ) {
      this.anemicProbability = 30;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2450,
      );
      return;
    }

    // Less Likely 5%
    // (RBC AND HGB AND HCT >= Opt OR >= Lab) AND
    // MCV = Opt AND RDW = Opt
    if (
      this.commonAnemiaConditions() &&
      this.mcv.isOptimal() &&
      this.rdw.isOptimal()
    ) {
      this.anemicProbability = 5;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2460,
      );
      return;
    }

    // Less Likely 5%
    // (RBC AND HGB AND HCT >= Opt OR >= Lab) AND
    // mcv OR rdw are blank
    if (
      this.commonAnemiaConditions() &&
      (this.mcv.isBlank() || this.rdw.isBlank())
    ) {
      this.anemicProbability = 5;
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_2470,
      );
      return;
    }
  }

  private commonAnemiaConditions?(): boolean {
    // (RBC >= OPT or RBC >= LAB) and
    // (HGB >= OPT or HGB >= LAB) and
    // (HCT >= OPT or HCT >= LAB)
    return (
      (this.rbc.isGreaterThanOrIsOptimal() ||
        this.rbc.isGreaterThanOrIsLab()) &&
      (this.hgb.isGreaterThanOrIsOptimal() ||
        this.hgb.isGreaterThanOrIsLab()) &&
      (this.hct.isGreaterThanOrIsOptimal() || this.hct.isGreaterThanOrIsLab())
    );
  }

  /**
   * Dehydration Probability	RBC	    HGB	    HCT
   * Likely       	        > Opt	> Opt	> Opt
   * Likely	                > Opt	= Opt	> Opt
   * Likely	                > Opt	> Opt	= Opt
   * Possible	                > Opt	= Opt	= Opt
   * Possible	                = Opt	>= Opt	> Opt
   */
  private calculateDehydrationProbabilty?() {
    // Likely
    // RBC > Opt AND HGB > Opt AND HCT > Opt
    // AND sodium, potassium, albumin, protein, bun are empty or <= Opt
    if (
      this.rbc.isGreaterThanOptimal() &&
      this.hgb.isGreaterThanOptimal() &&
      this.hct.isGreaterThanOptimal() &&
      this.isCmpOk()
    ) {
      this.dehydrationProbabilityCategory = ProbabilityCategory.LIKELY;
      return;
    }

    // Likely
    // RBC > Opt AND HGB = Opt AND HCT > Opt OR
    // RBC > Opt AND HGB > Opt AND HCT = Opt
    if (
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isOptimal() &&
        this.hct.isGreaterThanOptimal() &&
        this.isCmpOk()) ||
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isGreaterThanOptimal() &&
        this.hct.isOptimal() &&
        this.isCmpOk())
    ) {
      this.dehydrationProbabilityCategory = ProbabilityCategory.LIKELY;
      return;
    }

    // Possible
    // RBC > Opt AND HGB = Opt AND HCT = Opt OR
    // RBC = Opt AND (HGB >= Opt OR HCT > Opt)
    if (
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isOptimal() &&
        this.hct.isOptimal() &&
        this.isCmpOk()) ||
      (this.rbc.isOptimal() &&
        (this.hgb.isGreaterThanOptimal() || this.hct.isGreaterThanOptimal()) &&
        this.isCmpOk())
    ) {
      this.dehydrationProbabilityCategory = ProbabilityCategory.POSSIBLE;
      return;
    }
  }

  private isCmpOk?(): boolean {
    return (
      this.sodium.isBlank() &&
      this.albumin.isBlank() &&
      this.protein.isBlank() &&
      this.bun.isBlank()
    );
  }

  /**
   * Acute
   * Dehydration Probability	RBC	    HGB	    HCT     Sodium  Potassium Albumin Protein BUN
   * Highly Likely	        > Opt	> Opt	> Opt   ( > Opt or > Opt) <= Opt  <= Opt  <= Opt
   * Likely	                > Opt	= Opt	> Opt   ( > Opt or > Opt) <= Opt  <= Opt  <= Opt
   * Likely	                > Opt	> Opt	= Opt   ( > Opt or > Opt) <= Opt  <= Opt  <= Opt
   * Possible	                > Opt	= Opt	= Opt   ( > Opt or > Opt) <= Opt  <= Opt  <= Opt
   * Possible	                = Opt	>= Opt	> Opt   ( > Opt or > Opt) <= Opt  <= Opt  <= Opt
   */
  private calculateAcuteDehydrationProbabilty?() {
    // Highly Likely
    // RBC > Opt AND HGB > Opt AND HCT > Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND albumin <= Opt AND protein <= Opt AND bun <= Opt
    if (
      this.rbc.isGreaterThanOptimal() &&
      this.hgb.isGreaterThanOptimal() &&
      this.hct.isGreaterThanOptimal() &&
      this.sodium.isGreaterThanOptimal() &&
      (this.albumin.isBlank() || this.albumin.isLessThanOrIsOptimal()) &&
      (this.protein.isBlank() || this.protein.isLessThanOrIsOptimal()) &&
      this.bun.isLessThanOrIsOptimal()
    ) {
      this.acuteDehydrationProbabilityCategory =
        ProbabilityCategory.HIGHLY_LIKELY;
      return;
    }

    // Likely
    // RBC > Opt AND HGB = Opt AND HCT > Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND albumin <= Opt AND protein <= Opt AND bun <= Opt
    // OR
    // RBC > Opt AND HGB > Opt AND HCT = Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND albumin <= Opt AND protein <= Opt AND bun <= Opt
    if (
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isOptimal() &&
        this.hct.isGreaterThanOptimal() &&
        this.sodium.isGreaterThanOptimal() &&
        (this.albumin.isBlank() || this.albumin.isLessThanOrIsOptimal()) &&
        (this.protein.isBlank() || this.protein.isLessThanOrIsOptimal()) &&
        this.bun.isLessThanOrIsOptimal()) ||
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isGreaterThanOptimal() &&
        this.hct.isOptimal() &&
        this.sodium.isGreaterThanOptimal() &&
        (this.albumin.isBlank() || this.albumin.isLessThanOrIsOptimal()) &&
        (this.protein.isBlank() || this.protein.isLessThanOrIsOptimal()) &&
        this.bun.isLessThanOrIsOptimal())
    ) {
      this.acuteDehydrationProbabilityCategory = ProbabilityCategory.LIKELY;
      return;
    }

    // Possible
    // RBC > Opt AND HGB = Opt AND HCT = Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND albumin <= Opt AND protein <= Opt AND bun <= Opt
    // OR
    // RBC = Opt AND HGB >= Opt AND HCT >= Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND albumin <= Opt AND protein <= Opt AND bun <= Opt
    if (
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isOptimal() &&
        this.hct.isOptimal() &&
        this.sodium.isGreaterThanOptimal() &&
        (this.albumin.isBlank() || this.albumin.isLessThanOrIsOptimal()) &&
        (this.protein.isBlank() || this.protein.isLessThanOrIsOptimal()) &&
        this.bun.isLessThanOrIsOptimal()) ||
      (this.rbc.isOptimal() &&
        (this.hgb.isGreaterThanOptimal() || this.hct.isGreaterThanOptimal()) &&
        this.sodium.isGreaterThanOptimal() &&
        (this.albumin.isBlank() || this.albumin.isLessThanOrIsOptimal()) &&
        (this.protein.isBlank() || this.protein.isLessThanOrIsOptimal()) &&
        this.bun.isLessThanOrIsOptimal())
    ) {
      this.acuteDehydrationProbabilityCategory = ProbabilityCategory.POSSIBLE;
      return;
    }
  }

  /**
   * Chronic
   * Dehydration Probability	RBC	    HGB	     HCT     Sodium  Potassium   Albumin   Protein    BUN
   * Highly Likely	        > Opt	> Opt	 > Opt   ( > Opt or > Opt)   (> Opt or > Opt   or > Opt)
   * Likely	                > Opt	= Opt	 > Opt   ( > Opt or > Opt)   (> Opt or > Opt   or > Opt)
   * Likely	                > Opt	> Opt	 = Opt   ( > Opt or > Opt)   (> Opt or > Opt   or > Opt)
   * Possible	                > Opt	= Opt	 = Opt     N/A     N/A       (> Opt or > Opt   or > Opt)
   * Possible	                = Opt	>= Opt	>= Opt     N/A     N/A       (> Opt or > Opt   or > Opt)
   */
  private calculateChronicDehydrationProbabilty?() {
    // Highly Likely
    // RBC > Opt AND HGB > Opt AND HCT > Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND (albumin > Opt OR protein > Opt OR bun > Opt)
    if (
      this.rbc.isGreaterThanOptimal() &&
      this.hgb.isGreaterThanOptimal() &&
      this.hct.isGreaterThanOptimal() &&
      this.sodium.isGreaterThanOptimal() &&
      ((this.albumin.isBlank() &&
        this.protein.isBlank() &&
        this.bun.isGreaterThanOptimal()) ||
        this.albumin.isGreaterThanOptimal() ||
        this.protein.isGreaterThanOptimal() ||
        this.bun.isGreaterThanOptimal())
    ) {
      this.chronicDehydrationProbabilityCategory =
        ProbabilityCategory.HIGHLY_LIKELY;
      return;
    }

    // Likely
    // RBC > Opt AND HGB = Opt AND HCT > Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND albumin > Opt
    // AND (protein > Opt OR bun > Opt)
    // OR
    // RBC > Opt AND HGB > Opt AND HCT = Opt
    // AND (sodium > Opt OR potassium > Opt)
    // AND (albumin > Opt OR protein >= Opt OR bun >= Opt)
    if (
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isOptimal() &&
        this.hct.isGreaterThanOptimal() &&
        this.sodium.isGreaterThanOptimal() &&
        ((this.albumin.isBlank() &&
          this.protein.isBlank() &&
          this.bun.isGreaterThanOptimal()) ||
          this.albumin.isGreaterThanOptimal() ||
          this.protein.isGreaterThanOptimal() ||
          this.bun.isGreaterThanOptimal())) ||
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isGreaterThanOptimal() &&
        this.hct.isOptimal() &&
        this.sodium.isGreaterThanOptimal() &&
        ((this.albumin.isBlank() &&
          this.protein.isBlank() &&
          this.bun.isGreaterThanOptimal()) ||
          this.albumin.isGreaterThanOptimal() ||
          this.protein.isGreaterThanOptimal() ||
          this.bun.isGreaterThanOptimal()))
    ) {
      this.chronicDehydrationProbabilityCategory = ProbabilityCategory.LIKELY;
      return;
    }

    // Possible
    // RBC > Opt AND HGB = Opt AND HCT = Opt
    // AND (albumin > Opt OR protein <= Opt OR bun <= Opt)
    // OR
    // RBC = Opt AND HGB >= Opt AND HCT >= Opt
    // AND (albumin > Opt OR protein <= Opt OR bun <= Opt)
    if (
      (this.rbc.isGreaterThanOptimal() &&
        this.hgb.isOptimal() &&
        this.hct.isOptimal() &&
        this.sodium.isGreaterThanOptimal() &&
        ((this.albumin.isBlank() &&
          this.protein.isBlank() &&
          this.bun.isGreaterThanOptimal()) ||
          this.albumin.isGreaterThanOptimal() ||
          this.protein.isGreaterThanOptimal() ||
          this.bun.isGreaterThanOptimal())) ||
      (this.rbc.isOptimal() &&
        (this.hgb.isGreaterThanOptimal() || this.hct.isGreaterThanOptimal()) &&
        this.sodium.isGreaterThanOptimal() &&
        ((this.albumin.isBlank() &&
          this.protein.isBlank() &&
          this.bun.isGreaterThanOptimal()) ||
          this.albumin.isGreaterThanOptimal() ||
          this.protein.isGreaterThanOptimal() ||
          this.bun.isGreaterThanOptimal()))
    ) {
      this.chronicDehydrationProbabilityCategory = ProbabilityCategory.POSSIBLE;
      return;
    }
  }

  private calculatePoorOxygenationCategory?() {
    if (this.rbc.isGreaterThanOptimal()) {
      if (
        (this.hgb.isGreaterThanOptimal() && this.hct.isGreaterThanOptimal()) ||
        (this.hgb.isOptimal() && this.hct.isGreaterThanOptimal()) ||
        (this.hgb.isGreaterThanOptimal() && this.hct.isOptimal())
      ) {
        this.poorOxygenationProbabilityCategory = ProbabilityCategory.LIKELY;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_1300,
        );
      } else if (this.hgb.isOptimal() && this.hct.isOptimal()) {
        this.poorOxygenationProbabilityCategory = ProbabilityCategory.POSSIBLE;
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_1300,
        );
      }
    }
    return;
  }

  private calculatePoorOxgenationCategoryDueToSmoking?() {
    if (
      this.bloodwork.client.cigaretteSmoker &&
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_1300,
      )
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_1400,
      );
    }
  }

  private isAnemiaProbabilityPossibleOrHigher?(): boolean {
    return (
      this.anemicProbabilityCategory === ProbabilityCategory.POSSIBLE ||
      this.anemicProbabilityCategory === ProbabilityCategory.LIKELY ||
      this.anemicProbabilityCategory === ProbabilityCategory.HIGHLY_LIKELY
    );
  }

  private calculatePossibleRootCauses?() {
    this.calculatePossibleRootCausesWithRbcPanel();

    if (this.ferritin.hasValue()) {
      this.calculatePossibleRootCausesWithIronPanel();
      this.possibleRootCauseCategories =
        PossibleRootCauseCategory.removePossibleRootCausesDeterminedOnlyByRBC(
          this.possibleRootCauseCategories,
        );
    }
  }

  private calculatePossibleRootCausesWithRbcPanel?() {
    // Anemia Probability
    // Less Likely
    // Stop here since the subject is/should be in good health
    if (this.anemicProbabilityCategory === ProbabilityCategory.LESS_LIKELY) {
      return;
    }

    // RBC < Lab AND HGB < Opt AND HCT < Opt AND
    // RBC Size	                    RBC Color	    RDW
    // normocytic or macrocytic     normochromic	= Opt
    // AND wbc < Lab AND platelets < Lab
    if (
      this.rbc.isLessThanLab() &&
      this.hgb.isStricklyLessThanOptimal() &&
      this.hct.isStricklyLessThanOptimal() &&
      (this.redBloodCellSize === RedBloodCellSize.NORMAL ||
        this.redBloodCellSize === RedBloodCellSize.LARGE) &&
      this.redBloodCellColor === RedBloodCellColor.NORMAL &&
      this.rdw.isOptimal() &&
      this.wbc.isLessThanLab() &&
      this.platelets.isLessThanLab() &&
      (this.rpi.isBlank() || this.rpi.isLessThanOrIsOptimal())
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_1,
      );
    }

    // Anemia Probability	                RBC Size	RBC Color	    RDW
    // Possible, Likely, or Highly Likely	normocytic	normochromic	= Opt
    if (
      this.isAnemiaProbabilityPossibleOrHigher() &&
      this.redBloodCellSize === RedBloodCellSize.NORMAL &&
      this.redBloodCellColor === RedBloodCellColor.NORMAL
    ) {
      if (this.rdw.isOptimal()) {
        if (this.rpi.isBlank() || this.rpi.isLessThanOrIsOptimal()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_2,
          );
        }
        const HAS_ROOT_CAT1 =
          PossibleRootCauseCategory.hasAnyRootCauseCategories(
            this.possibleRootCauseCategories,
            RootCauseCategory.CATEGORY_1,
          );
        let HAS_ROOT_CAT22: boolean;
        if (this.rpi.isBlank()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_21,
          );
        } else if (this.rpi.isGreaterThanOptimal()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_22,
          );
          HAS_ROOT_CAT22 = true;
        }
        if (!HAS_ROOT_CAT22) {
          if (this.tsh.isBlank()) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_23,
            );
          }
          // TSH > Opt OR
          // freeT3 < Opt OR
          // freeT4 < Opt
          if (
            this.tsh.isGreaterThanOptimal() ||
            this.t3Free.isStricklyLessThanOptimal() ||
            this.t4Free.isStricklyLessThanOptimal()
          ) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_24,
            );
          }
        }

        // Only add CAT 25 if (CAT1 and CAT22) is not true
        if (!HAS_ROOT_CAT1 && !HAS_ROOT_CAT22) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_25,
          );
        }
        if (
          (this.alp.isBlank() || this.alp.isLessThanMean()) &&
          (this.albumin.isBlank() || this.albumin.isLessThanMean())
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_26,
          );
        }
      }
    }

    // Anemia Probability	                RBC Size	RBC Color	    RDW
    // Possible, Likely, or Highly Likely	normocytic	normochromic	> Opt
    if (
      this.isAnemiaProbabilityPossibleOrHigher() &&
      this.redBloodCellSize === RedBloodCellSize.NORMAL &&
      this.redBloodCellColor === RedBloodCellColor.NORMAL
    ) {
      if (this.rdw.isGreaterThanOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_3,
        );
      }

      if (
        this.bun.isGreaterThanOptimal() &&
        this.creatinine.isGreaterThanOptimal() &&
        this.eGFR.isStricklyLessThanOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_31,
        );
      }

      if (
        (this.alp.isBlank() || this.alp.isLessThanMean()) &&
        (this.albumin.isBlank() || this.albumin.isLessThanMean()) &&
        this.rdw.isGreaterThanOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_32,
        );
      }

      if (
        this.rdw.isGreaterThanOptimal() && // If Category 3 is true
        (this.bun.isBlank() || this.bun.isGreaterThanOrIsOptimal()) &&
        !this.globulin.isBlank() &&
        this.globulin.isNotOptimal() &&
        (this.albumin.isLessThanOrIsOptimal() ||
          this.protein.isLessThanOrIsOptimal()) &&
        (this.phosphorus.isBlank() || this.phosphorus.isLessThanOrIsOptimal())
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_33,
        );
        if (this.alp.isStricklyLessThanOptimal()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_34,
          );
        }
      }
    }

    // Anemia Probability
    // Possible, Likely, or Highly Likely
    // AND
    // RBC Size	    RBC Color
    // microcytic	hypochromic  OR
    // microcytic	normochromic OR
    // normocytic	hypochromic
    if (
      this.isAnemiaProbabilityPossibleOrHigher() &&
      ((this.redBloodCellSize === RedBloodCellSize.SMALL &&
        this.redBloodCellColor === RedBloodCellColor.PALE) ||
        (this.redBloodCellSize === RedBloodCellSize.SMALL &&
          this.redBloodCellColor === RedBloodCellColor.NORMAL) ||
        (this.redBloodCellSize === RedBloodCellSize.NORMAL &&
          this.redBloodCellColor === RedBloodCellColor.PALE))
    ) {
      if (this.rdw.isOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_4,
        );
      }
    }

    const HAS_ROOT_CAT_4 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_4,
    );

    // Anemia Probability
    // Possible, Likely, or Highly Likely
    // AND
    // RBC Size	    RBC Color
    // microcytic	hypochromic  OR
    // microcytic	normochromic OR
    // normocytic	hypochromic
    // AND
    // RDW > Opt
    if (
      this.isAnemiaProbabilityPossibleOrHigher() &&
      ((this.redBloodCellSize === RedBloodCellSize.SMALL &&
        this.redBloodCellColor === RedBloodCellColor.PALE) ||
        (this.redBloodCellSize === RedBloodCellSize.SMALL &&
          this.redBloodCellColor === RedBloodCellColor.NORMAL) ||
        (this.redBloodCellSize === RedBloodCellSize.NORMAL &&
          this.redBloodCellColor === RedBloodCellColor.PALE)) &&
      this.rdw.isGreaterThanOptimal()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_5,
      );
    }

    const HAS_ROOT_CAT_5 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_5,
    );

    if (HAS_ROOT_CAT_4 || HAS_ROOT_CAT_5) {
      if (
        (this.alp.isBlank() || this.alp.isLessThanMean()) &&
        (this.albumin.isBlank() || this.albumin.isLessThanMean())
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_51,
        );
      }
      if (
        this.ast.isGreaterThanOrIsOptimal() &&
        this.alt.isGreaterThanOrIsOptimal() &&
        this.alp.isGreaterThanOrIsOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_52,
        );
      } else if (
        (this.ast.isStricklyLessThanOptimal() ||
          this.alt.isStricklyLessThanOptimal()) &&
        this.alp.isGreaterThanOrIsOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_53,
        );
      } else if (
        this.ast.isGreaterThanOrIsOptimal() &&
        this.alt.isGreaterThanOrIsOptimal() &&
        this.alp.isStricklyLessThanOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_54,
        );
      } else if (
        (this.ast.isStricklyLessThanOptimal() ||
          this.alt.isStricklyLessThanOptimal()) &&
        this.alp.isStricklyLessThanOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_55,
        );
      }
      if (
        this.bilirubin.isGreaterThanOptimal() &&
        (this.bilirubinIndirect.isBlank() ||
          this.bilirubinIndirect.isGreaterThanOptimal())
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_56,
        );
      }
      if (this.rpi.isBlank()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_57,
        );
      } else if (this.rpi.isGreaterThanOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_58,
        );
      }
    }

    if (
      (HAS_ROOT_CAT_4 || HAS_ROOT_CAT_5) &&
      this.ast.isBlank() &&
      this.alt.isBlank() &&
      this.alp.isBlank()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_59,
      );
    }

    // Category 6, 63, 64
    if (this.isAnemiaProbabilityPossibleOrHigher()) {
      if (
        (this.redBloodCellSize === RedBloodCellSize.SMALL ||
          this.redBloodCellSize === RedBloodCellSize.NORMAL) &&
        this.redBloodCellColor === RedBloodCellColor.DARK
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_6,
        );
      }
    }

    if (
      this.rbc.isStricklyLessThanOptimal() &&
      this.redBloodCellSize === RedBloodCellSize.SMALL &&
      this.redBloodCellColor === RedBloodCellColor.DARK &&
      this.rdw.isOptimal() &&
      (this.bilirubin.isBlank() || this.bilirubin.isGreaterThanOptimal()) &&
      (this.bilirubinIndirect.isBlank() ||
        this.bilirubinIndirect.isGreaterThanOptimal())
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_63,
      );
    }

    // Category 64 and 65
    if (
      this.rbc.isStricklyLessThanOptimal() &&
      this.rdw.isOptimal() &&
      (this.bilirubin.isBlank() || this.bilirubin.isGreaterThanOptimal()) &&
      (this.bilirubinIndirect.isBlank() ||
        this.bilirubinIndirect.isGreaterThanOptimal())
    ) {
      if (this.redBloodCellSize === RedBloodCellSize.NORMAL) {
        if (this.rpi.isBlank()) {
          if (this.redBloodCellColor === RedBloodCellColor.DARK) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_64,
            );
          } else if (this.redBloodCellColor === RedBloodCellColor.NORMAL) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_65,
            );
          }
        }
        if (this.redBloodCellColor === RedBloodCellColor.DARK) {
          if (this.rpi.isGreaterThanOptimal() && this.rpi.isLessThanOrIsLab()) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_66,
            );
          } else if (this.rpi.isGreaterThanLab()) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_67,
            );
          }
        } else if (this.redBloodCellColor === RedBloodCellColor.NORMAL) {
          if (this.rpi.isGreaterThanOptimal() && this.rpi.isLessThanOrIsLab()) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_68,
            );
          } else if (this.rpi.isGreaterThanLab()) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_69,
            );
          }
        }
      }
    }
    /*OLD SECTION BEFORE ADDING WBC+NLR PATTERNS
    // Category 70,71,72,73
    if (this.isAnemiaProbabilityPossibleOrHigher()) {
      if (
        this.redBloodCellSize === RedBloodCellSize.SMALL ||
        this.redBloodCellSize === RedBloodCellSize.NORMAL
      ) {
        if (this.wbc.isBlank()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_70, //Immune System Abnormality
          );
        } else if (this.wbc.isStricklyLessThanOptimal()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_71, //Suppressed Immune Activity
          );
        } else if (this.wbc.isGreaterThanOptimal()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_72, //Acute Immune Activity
          );
        } else if (
          this.wbc.isOptimal() &&
          this.wbcAnalysis.immuneSystemDisorderProbability >= 50
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_73, //Immune System Abnormality
          );
        }
      }
    }*/

    // --- Immune patterns (WBC-only 70–73; WBC+NLR 74–77) ---
    // Place this where you currently assign CATEGORY_70..73

    // Only evaluate if anemia probability is Possible or higher AND RBC size is small or normal.
    // (Keep these guards consistent with your file's existing checks. If you don't have them,
    // you can remove this outer if.)
    if (this.isAnemiaProbabilityPossibleOrHigher()) {
      if (
        this.redBloodCellSize === RedBloodCellSize.SMALL ||
        this.redBloodCellSize === RedBloodCellSize.NORMAL
      ) {
        const hasNlr = this.nlr?.hasValue?.() === true;

        // -------------------- Prefer WBC+NLR when differentials exist --------------------
        if (hasNlr) {
          const wbcHigh =
            this.wbc?.isGreaterThanLab?.() ||
            this.wbc?.isGreaterThanOptimal?.();
          const wbcLow =
            this.wbc?.isLessThanLab?.() ||
            this.wbc?.isStricklyLessThanOptimal?.();
          const nlrHigh =
            this.nlr?.isGreaterThanLab?.() ||
            this.nlr?.isGreaterThanOptimal?.();
          const nlrLow =
            this.nlr?.isLessThanLab?.() ||
            this.nlr?.isStricklyLessThanOptimal?.();

          // 74: Acute Innate (WBC ↑ & NLR ↑)
          if (wbcHigh && nlrHigh) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_74,
            );
          }
          // 75: Acute Adaptive (WBC ↑ & NLR ↓)
          else if (wbcHigh && nlrLow) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_75,
            );
          }
          // 76: Chronic Innate (WBC ↓ & NLR ↑)
          else if (wbcLow && nlrHigh) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_76,
            );
          }
          // 77: Chronic Adaptive (WBC ↓ & NLR ↓)
          else if (wbcLow && nlrLow) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_77,
            );
          }

          // Note: when NLR exists, we do not also push the WBC-only fallbacks (70–73)
          // to keep the output specific to the combined pattern.
        }

        // --- RBC Insights: WBC optimal + NLR non-optimal → 78/79 ---
        if (this.wbc?.isOptimal?.() && this.nlr?.hasValue?.()) {
          const nlrHigh =
            this.nlr?.isGreaterThanLab?.() ||
            this.nlr?.isGreaterThanOptimal?.();
          const nlrLow =
            this.nlr?.isLessThanLab?.() ||
            this.nlr?.isLessThanOptimal?.() ||
            this.nlr?.isStricklyLessThanOptimal?.();

          if (nlrHigh) {
            // Innate Immune Response
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_78,
            );
          } else if (nlrLow) {
            // Adaptive Immune Response
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_79,
            );
          }
        }

        // -------------------- WBC-only fallback (no differentials) --------------------
        // -------------------- WBC-only fallback (no differentials OR NLR optimal) --------------------
        const nlrOptimal = hasNlr && this.nlr?.isOptimal?.();
        if (!hasNlr || nlrOptimal) {
          if (this.wbc?.isBlank?.()) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_70,
            ); // Immune System Abnormality (unknown WBC)
          } else if (
            this.wbc?.isStricklyLessThanOptimal?.() ||
            this.wbc?.isLessThanLab?.()
          ) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_71,
            ); // Suppressed Immune Activity
          } else if (
            this.wbc?.isGreaterThanOptimal?.() ||
            this.wbc?.isGreaterThanLab?.()
          ) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_72,
            ); // Acute Immune Activity
          } else if (
            this.wbc?.isOptimal?.() &&
            this.wbcAnalysis?.immuneSystemDisorderProbability >= 50
          ) {
            this.possibleRootCauseCategories.push(
              PossibleRootCauseCategory.CATEGORY_73,
            ); // Immune System Abnormality (probability-based)
          }
        }
      }
    }

    // Category 80
    if (
      (HAS_ROOT_CAT_4 || HAS_ROOT_CAT_5) &&
      (this.bun.isBlank() || this.bun.isGreaterThanOrIsOptimal()) &&
      (this.phosphorus.isBlank() || this.phosphorus.isLessThanOrIsOptimal()) &&
      this.globulin.isNotOptimal() &&
      (this.albumin.isLessThanOrIsOptimal() ||
        this.protein.isLessThanOrIsOptimal())
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_80,
      );
    }

    if (this.isAnemiaProbabilityPossibleOrHigher()) {
      if (this.redBloodCellSize === RedBloodCellSize.LARGE) {
        if (
          (this.bun.isBlank() || this.bun.isGreaterThanOrIsOptimal()) &&
          this.globulin.hasValue() &&
          this.globulin.isNotOptimal() &&
          (this.albumin.isLessThanOrIsOptimal() ||
            this.protein.isLessThanOrIsOptimal()) &&
          (this.phosphorus.isBlank() || this.phosphorus.isLessThanOrIsOptimal())
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_81,
          );
        } else {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_8,
          );
        }
      }
    }

    const HAS_ROOT_CAT_80 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_80,
    );
    const HAS_ROOT_CAT_81 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_81,
    );

    if (
      (HAS_ROOT_CAT_80 || HAS_ROOT_CAT_81) &&
      this.alp.isStricklyLessThanOptimal()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_82,
      );
    }

    if (
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_33,
        RootCauseCategory.CATEGORY_80,
        RootCauseCategory.CATEGORY_81,
      )
    ) {
      if (this.carbonDioxide.isStricklyLessThanOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_83,
        );
        if (HAS_ROOT_CAT_81) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_84,
          );
        }
      }
      if (this.chloride.isStricklyLessThanOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_85,
        );
        if (
          this.sodium.isStricklyLessThanOptimal() &&
          this.potassium.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_86,
          );
        }
      }
    }

    if (this.isAnemiaProbabilityPossibleOrHigher()) {
      if (
        this.redBloodCellSize === RedBloodCellSize.LARGE &&
        (this.redBloodCellColor === RedBloodCellColor.NORMAL ||
          this.redBloodCellColor === RedBloodCellColor.PALE) &&
        (this.alp.isBlank() || this.alp.isLessThanMean()) &&
        (this.albumin.isBlank() || this.albumin.isLessThanMean())
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_9,
        );
      }
    }

    if (this.redBloodCellSize === RedBloodCellSize.LARGE) {
      if (
        this.hgb.isStricklyLessThanOptimal() &&
        this.neutrophilsAbs.isStricklyLessThanOptimal() &&
        (this.redBloodCellColor === RedBloodCellColor.PALE ||
          this.redBloodCellColor === RedBloodCellColor.NORMAL)
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_91,
        );
      }
      if (this.isAnemiaProbabilityPossibleOrHigher()) {
        if (this.tsh.isBlank()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_92,
          );
        }
        // Only add 93 if client is an alcoholDrinker
        if (this.bloodwork.client.alcoholDrinker) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_93,
          );
        }
        // TSH > Opt OR
        // freeT3 < Opt OR
        // freeT4 < Opt
        if (
          this.tsh.isGreaterThanOptimal() ||
          this.t3Free.isStricklyLessThanOptimal() ||
          this.t4Free.isStricklyLessThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_94,
          );
        }
      }
    }

    const HAS_CAT_92 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_92,
    );
    const HAS_CAT_94 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_94,
    );

    // if both category 92 and 94 are TRUE then remove category 92
    if (HAS_CAT_92 && HAS_CAT_94) {
      this.possibleRootCauseCategories =
        PossibleRootCauseCategory.removePossibleRootCauseCategories(
          this.possibleRootCauseCategories,
          RootCauseCategory.CATEGORY_92,
        );
    }
  }

  private calculatePossibleRootCausesWithIronPanel?() {
    const HAS_ROOT_CAT_1 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_1,
    );

    const HAS_ROOT_CAT_2 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_2,
    );

    const HAS_ROOT_CAT_3 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_3,
    );

    const HAS_ROOT_CAT_4 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_4,
    );

    const HAS_ROOT_CAT_5 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_5,
    );

    const HAS_ROOT_CAT_2_OR_3 = HAS_ROOT_CAT_2 || HAS_ROOT_CAT_3;
    const HAS_ROOT_CAT_4_OR_5 = HAS_ROOT_CAT_4 || HAS_ROOT_CAT_5;

    const HAS_ROOT_CAT_21 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_21,
    );

    const HAS_ROOT_CAT_22 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_22,
    );

    const HAS_ROOT_CAT_23 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_23,
    );

    const HAS_ROOT_CAT_24 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_24,
    );

    const HAS_ROOT_CAT_25 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_25,
    );

    const HAS_ROOT_CAT_26 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_26,
    );

    if (this.COMMON_IRON_TIBC_SATURATION_FERRITIN) {
      if (HAS_ROOT_CAT_2_OR_3) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_200,
        );
      }
    }

    if (
      (this.iron.isBlank() || this.iron.isOptimal()) &&
      (this.tibc.isBlank() || this.tibc.isOptimal()) &&
      (this.saturation.isBlank() || this.saturation.isOptimal()) &&
      this.ferritin.isOptimal()
    ) {
      if (HAS_ROOT_CAT_21) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_201,
        );
      }
      if (HAS_ROOT_CAT_22) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_202,
        );
      } else {
        if (HAS_ROOT_CAT_23) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_203,
          );
        }
        if (HAS_ROOT_CAT_24) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_204,
          );
        }
      }
      if (!HAS_ROOT_CAT_1 && !HAS_ROOT_CAT_22) {
        if (HAS_ROOT_CAT_25) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_205,
          );
        }
        if (HAS_ROOT_CAT_26) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_206,
          );
        }
      }
    }

    // HAS_ROOT_CAT_2_OR_3 AND
    // iron <= Opt AND tibc >= Opt AND saturation < Opt AND ferritin < Opt
    // neutrophilsAbs is BLANK or >= Opt
    if (
      HAS_ROOT_CAT_2_OR_3 &&
      this.iron.isLessThanOrIsOptimal() &&
      this.tibc.isGreaterThanOrIsOptimal() &&
      this.ferritin.isStricklyLessThanOptimal() &&
      (!this.neutrophilsAbs.hasValue() ||
        this.neutrophilsAbs.isGreaterThanOrIsOptimal())
    ) {
      // saturation < Opt
      if (this.saturation.isStricklyLessThanOptimal()) {
        if (this.bloodwork.plateletMCHRatio() <= 12) {
          // platelet / MCH Ratio <= 12
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_300,
          );
        } else {
          // platelet / MCH Ratio > 12 or null
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_301,
          );
        }
        // saturation = Opt
      } else if (this.saturation.isOptimal()) {
        if (this.bloodwork.plateletMCHRatio() <= 12) {
          // platelet / MCH Ratio <= 12
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_302,
          );
        } else {
          // platelet / MCH Ratio > 12 or null
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_303,
          );
        }
      }
    }

    // HAS_ROOT_CAT_2_OR_3
    // iron BLANK OR <= Opt &&
    // tibc BLANK &&
    // saturation BLANK
    // ferritin < Opt
    // neutrophilsAbs is BLANK or >= Opt
    if (
      HAS_ROOT_CAT_2_OR_3 &&
      (!this.iron.hasValue() || this.iron.isLessThanOrIsOptimal()) &&
      !this.tibc.hasValue() &&
      !this.saturation.hasValue() &&
      this.ferritin.isStricklyLessThanOptimal() &&
      (!this.neutrophilsAbs.hasValue() ||
        this.neutrophilsAbs.isGreaterThanOrIsOptimal())
    ) {
      if (this.bloodwork.plateletMCHRatio() <= 12) {
        // platelet / MCH Ratio <= 12
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_304,
        );
      } else {
        // platelet / MCH Ratio > 12 or null
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_305,
        );
      }
    }

    // HAS_ROOT_CAT_2_OR_3 AND
    // iron <= Opt AND tibc >= Opt AND saturation < Opt AND ferritin < Opt
    // neutrophilsAbs < Opt
    if (
      HAS_ROOT_CAT_2_OR_3 &&
      this.iron.isLessThanOrIsOptimal() &&
      this.tibc.isGreaterThanOrIsOptimal() &&
      this.ferritin.isStricklyLessThanOptimal() &&
      this.neutrophilsAbs.isStricklyLessThanOptimal()
    ) {
      // saturation < Opt
      if (this.saturation.isStricklyLessThanOptimal()) {
        if (this.bloodwork.plateletMCHRatio() <= 12) {
          // platelet / MCH Ratio <= 12
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_306,
          );
        } else {
          // platelet / MCH Ratio > 12 or null
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_307,
          );
        }
        // saturation = Opt
      } else if (this.saturation.isOptimal()) {
        if (this.bloodwork.plateletMCHRatio() <= 12) {
          // platelet / MCH Ratio <= 12
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_308,
          );
        } else {
          // platelet / MCH Ratio > 12 or null
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_309,
          );
        }
      }
    }

    // HAS_ROOT_CAT_2_OR_3
    // iron BLANK OR <= Opt &&
    // tibc BLANK &&
    // saturation BLANK
    // ferritin < Opt
    // neutrophilsAbs < Opt
    if (
      HAS_ROOT_CAT_2_OR_3 &&
      (!this.iron.hasValue() || this.iron.isLessThanOrIsOptimal()) &&
      !this.tibc.hasValue() &&
      !this.saturation.hasValue() &&
      this.ferritin.isStricklyLessThanOptimal() &&
      this.neutrophilsAbs.isStricklyLessThanOptimal()
    ) {
      if (this.bloodwork.plateletMCHRatio() <= 12) {
        // platelet / MCH Ratio <= 12
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_310,
        );
      } else {
        // platelet / MCH Ratio > 12 or null
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_311,
        );
      }
    }
    const HAS_CAT_33 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_33,
    );

    // If Category 33 AND (301 OR 307) are TRUE
    if (
      HAS_CAT_33 &&
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_301,
        RootCauseCategory.CATEGORY_307,
      )
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_312,
      );
    }

    // If Category 33 AND (303, 305, 309, OR 311) are TRUE
    if (
      HAS_CAT_33 &&
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_303,
        RootCauseCategory.CATEGORY_305,
        RootCauseCategory.CATEGORY_309,
        RootCauseCategory.CATEGORY_311,
      )
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_313,
      );
    }

    const HAS_CAT_32 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_32,
    );
    if (HAS_CAT_32) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_314,
      );
    }

    // HAS_ROOT_CAT_4_OR_5
    // iron <= Opt AND tibc >= Opt AND saturation < Opt AND ferritin < Opt
    if (
      HAS_ROOT_CAT_4_OR_5 &&
      this.iron.isLessThanOrIsOptimal() &&
      this.tibc.isGreaterThanOrIsOptimal() &&
      this.saturation.isStricklyLessThanOptimal() &&
      this.ferritin.isStricklyLessThanOptimal()
    ) {
      // Absolute Neutrophils BLANK OR >= Opt
      // globulin is BLANK OR = Opt
      if (
        this.neutrophilsAbs.isBlank() ||
        this.neutrophilsAbs.isGreaterThanOrIsOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_400,
        );
      }
    }

    // HAS_ROOT_CAT_4_OR_5 AND
    // iron <= Opt AND
    // tibc >= Opt AND
    // sat  <  Opt AND
    // ferritin < Opt
    // neutrophilsAbs < Opt
    if (
      HAS_ROOT_CAT_4_OR_5 &&
      this.iron.isLessThanOrIsOptimal() &&
      this.tibc.isGreaterThanOrIsOptimal() &&
      this.saturation.isStricklyLessThanOptimal() &&
      this.ferritin.isStricklyLessThanOptimal() &&
      this.neutrophilsAbs.isStricklyLessThanOptimal()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_401,
      );
    }

    const HAS_CAT_80 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_80,
    );

    if (
      (HAS_CAT_80 &&
        PossibleRootCauseCategory.hasAnyRootCauseCategories(
          this.possibleRootCauseCategories,
          RootCauseCategory.CATEGORY_400,
          RootCauseCategory.CATEGORY_401,
        )) ||
      (HAS_CAT_33 &&
        PossibleRootCauseCategory.hasAnyRootCauseCategories(
          this.possibleRootCauseCategories,
          RootCauseCategory.CATEGORY_300,
          RootCauseCategory.CATEGORY_306,
        ))
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_402,
      );
    }

    if (HAS_ROOT_CAT_4_OR_5) {
      const ferritinAdequate =
        this.ferritin?.isGreaterThanOrIsOptimal?.() === true;
      const ferritinLow =
        (this.ferritin?.isLessThanOrIsOptimal?.() ?? false) ||
        (this.ferritin?.isLessThanLab?.() ?? false);

      const ironLow = this.iron?.hasValue?.()
        ? (this.iron.isLessThanLab?.() ?? false) ||
          (this.iron.isLessThanOrIsOptimal?.() ?? false)
        : false;

      const satLow = this.saturation?.hasValue?.()
        ? (this.saturation.isLessThanLab?.() ?? false) ||
          (this.saturation.isLessThanOrIsOptimal?.() ?? false)
        : false;

      const mobilizationBlock = ironLow || satLow;

      const vitAHas = this.vitaminA?.hasValue?.() === true;
      const vitA_belowLab = vitAHas
        ? (this.vitaminA.isLessThanLab?.() ?? false)
        : false;
      const vitA_belowOptimal = vitAHas
        ? (this.vitaminA.isLessThanOrIsOptimal?.() ?? false)
        : false;

      let vitaminA_trigger = false;

      // Ferritin adequate → mobilization pattern
      if (ferritinAdequate && vitAHas) {
        vitaminA_trigger =
          vitA_belowLab || (vitA_belowOptimal && mobilizationBlock);
      }
      // Ferritin low → contributor if vit A < lab or < optimal
      if (ferritinLow && vitAHas) {
        vitaminA_trigger =
          vitaminA_trigger || vitA_belowLab || vitA_belowOptimal;
      }

      if (vitaminA_trigger) {
        if (
          !this.possibleRootCauseCategories.includes(
            PossibleRootCauseCategory.CATEGORY_425,
          )
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_425,
          );
        }
      }
    }

    if (
      (this.iron.isBlank() || this.iron.isLessThanOrIsOptimal()) &&
      this.tibc.isEmptyOrIsLessThanOrIsOptimal() &&
      (this.saturation.isBlank() ||
        this.saturation.isGreaterThanOrIsOptimal()) &&
      this.ferritin.isGreaterThanOrIsOptimal()
    ) {
      if (HAS_ROOT_CAT_4_OR_5) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_408,
        );
      }
    }

    if (
      (this.iron.isBlank() || this.iron.isLessThanOrIsOptimal()) &&
      this.tibc.isEmptyOrIsLessThanOrIsOptimal() &&
      this.saturation.isStricklyLessThanOptimal() &&
      this.ferritin.isGreaterThanOrIsOptimal()
    ) {
      if (HAS_ROOT_CAT_4_OR_5) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_409,
        );
        if (
          PossibleRootCauseCategory.hasAnyRootCauseCategories(
            this.possibleRootCauseCategories,
            RootCauseCategory.CATEGORY_71,
          )
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_410,
          );
        }
        if (
          PossibleRootCauseCategory.hasAnyRootCauseCategories(
            this.possibleRootCauseCategories,
            RootCauseCategory.CATEGORY_72,
          )
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_411,
          );
        }
      }
    }

    const HAS_ROOT_CAT_56: boolean =
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_56,
      );
    if (
      HAS_ROOT_CAT_4_OR_5 &&
      HAS_ROOT_CAT_56 &&
      this.commonIronPanelEvaluation() &&
      this.ferritin.isGreaterThanOptimal()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_412,
      );
    }

    const HAS_ROOT_CAT_42 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_42,
    );
    if (
      HAS_ROOT_CAT_42 &&
      this.iron.isGreaterThanOrIsOptimal() &&
      this.tibc.isLessThanOrIsOptimal() &&
      this.saturation.isGreaterThanOptimal() &&
      this.ferritin.isGreaterThanOrIsOptimal()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_414,
      );
    }

    // HAS_ROOT_CAT_4_OR_5 &&
    // iron <= Opt
    // tibc >= Opt
    // sat  =  Opt
    // ferritin < Opt
    if (HAS_ROOT_CAT_4_OR_5) {
      if (
        this.iron.isLessThanOrIsOptimal() &&
        this.ferritin.isStricklyLessThanOptimal() &&
        this.tibc.isGreaterThanOrIsOptimal() &&
        this.saturation.isOptimal()
      ) {
        if (
          this.neutrophilsAbs.isBlank() ||
          this.neutrophilsAbs.isGreaterThanOrIsOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_415,
          );
        } else if (this.neutrophilsAbs.isStricklyLessThanOptimal()) {
          // neutrophilsAbs < Opt
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_416,
          );
        }
      }

      if (
        (this.iron.isBlank() || this.iron.isLessThanOrIsOptimal()) &&
        this.tibc.isBlank() &&
        this.saturation.isBlank() &&
        this.ferritin.isStricklyLessThanOptimal()
      ) {
        if (
          this.neutrophilsAbs.isBlank() ||
          this.neutrophilsAbs.isGreaterThanOrIsOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_417,
          );
        } else if (this.neutrophilsAbs.isStricklyLessThanOptimal()) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_418,
          );
        }
      }
    }

    const HAS_ROOT_CAT_80 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_80,
    );

    const HAS_ROOT_CAT_400S =
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_415,
        RootCauseCategory.CATEGORY_416,
        RootCauseCategory.CATEGORY_417,
        RootCauseCategory.CATEGORY_418,
      );

    const HAS_ROOT_CAT_300S =
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_302,
        RootCauseCategory.CATEGORY_304,
        RootCauseCategory.CATEGORY_308,
        RootCauseCategory.CATEGORY_310,
      );

    if (
      (HAS_ROOT_CAT_80 && HAS_ROOT_CAT_400S) ||
      (HAS_CAT_33 && HAS_ROOT_CAT_300S)
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_419,
      );
    }

    if (
      HAS_ROOT_CAT_4_OR_5 &&
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_73,
      )
    ) {
      if (
        (this.iron.isBlank() || this.iron.isLessThanOrIsOptimal()) &&
        (this.tibc.isBlank() || this.tibc.isLessThanOrIsOptimal()) &&
        this.saturation.isStricklyLessThanOptimal() &&
        this.ferritin.isGreaterThanOrIsOptimal()
      ) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_420,
        );
      }
    }

    const HAS_ROOT_CAT_51 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_51,
    );
    if (
      HAS_ROOT_CAT_51 &&
      this.iron.isStricklyLessThanOptimal() &&
      this.ferritin.hasValue()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_423,
      );
    }

    // Category 500's
    const HAS_ROOT_CAT_52 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_52,
    );

    const HAS_ROOT_CAT_53 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_53,
    );

    const HAS_ROOT_CAT_54 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_54,
    );

    const HAS_ROOT_CAT_55 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_55,
    );

    const HAS_ROOT_CAT_57 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_57,
    );

    const HAS_ROOT_CAT_58 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_58,
    );

    const HAS_ROOT_CAT_59 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_59,
    );

    if (this.commonIronPanelEvaluation()) {
      if (HAS_ROOT_CAT_52) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_500,
        );
      }
      if (HAS_ROOT_CAT_53) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_501,
        );
      }
      if (HAS_ROOT_CAT_54) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_502,
        );
      }
      if (HAS_ROOT_CAT_55) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_503,
        );
      }
      if (HAS_ROOT_CAT_59) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_509,
        );
      }
    }

    if (
      (this.iron.isBlank() || this.iron.isStricklyLessThanOptimal()) &&
      (this.tibc.isBlank() || this.tibc.isGreaterThanOptimal()) &&
      (this.saturation.isBlank() ||
        this.saturation.isStricklyLessThanOptimal()) &&
      this.ferritin.isLessThanOrIsOptimal()
    ) {
      if (HAS_ROOT_CAT_57) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_507,
        );
      }
      if (HAS_ROOT_CAT_58) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_508,
        );
      }
    }

    // Category 600s
    const HAS_ROOT_CAT_63 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_63,
    );
    const HAS_ROOT_CAT_64 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_64,
    );
    const HAS_ROOT_CAT_65 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_65,
    );
    const HAS_ROOT_CAT_66 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_66,
    );
    const HAS_ROOT_CAT_67 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_67,
    );
    const HAS_ROOT_CAT_68 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_68,
    );
    const HAS_ROOT_CAT_69 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_69,
    );

    if (
      (this.iron.isBlank() || this.iron.isGreaterThanOptimal()) &&
      (this.tibc.isBlank() || this.tibc.isLessThanOrIsOptimal()) &&
      (this.saturation.isBlank() || this.saturation.isGreaterThanOptimal()) &&
      this.ferritin.isGreaterThanOptimal()
    ) {
      if (HAS_ROOT_CAT_63) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_603,
        );
      }
      if (HAS_ROOT_CAT_64) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_604,
        );
      }
      if (HAS_ROOT_CAT_65) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_605,
        );
      }
      if (HAS_ROOT_CAT_66) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_606,
        );
      }
      if (HAS_ROOT_CAT_67) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_607,
        );
      }
      if (HAS_ROOT_CAT_68) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_608,
        );
      }
      if (HAS_ROOT_CAT_69) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_609,
        );
      }
    }

    // Category 700s
    if (
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_70,
      )
    ) {
      if (this.COMMON_IRON_TIBC_SATURATION_FERRITIN) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_700,
        );
      }
    }

    if (
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_71,
      )
    ) {
      if (this.COMMON_IRON_TIBC_SATURATION_FERRITIN) {
        if (
          !PossibleRootCauseCategory.hasAnyRootCauseCategories(
            this.possibleRootCauseCategories,
            RootCauseCategory.CATEGORY_410,
          )
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_701,
          );
        }
      }
    }

    if (this.COMMON_IRON_TIBC_SATURATION_FERRITIN) {
      if (
        !PossibleRootCauseCategory.hasAnyRootCauseCategories(
          this.possibleRootCauseCategories,
          RootCauseCategory.CATEGORY_411,
        )
      ) {
        if (
          PossibleRootCauseCategory.hasAnyRootCauseCategories(
            this.possibleRootCauseCategories,
            RootCauseCategory.CATEGORY_72,
          )
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_702,
          );
        }
      }
      if (
        !PossibleRootCauseCategory.hasAnyRootCauseCategories(
          this.possibleRootCauseCategories,
          RootCauseCategory.CATEGORY_420,
        )
      ) {
        if (
          PossibleRootCauseCategory.hasAnyRootCauseCategories(
            this.possibleRootCauseCategories,
            RootCauseCategory.CATEGORY_73,
          )
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_703,
          );
        }
      }
    }

    // Category 800s
    const COMMON_800 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_312,
      RootCauseCategory.CATEGORY_313,
      RootCauseCategory.CATEGORY_402,
      RootCauseCategory.CATEGORY_419,
    );

    const HAS_CAT_8 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_8,
    );
    const HAS_CAT_34 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_34,
    );
    const HAS_CAT_81 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_81,
    );
    const HAS_CAT_82 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_82,
    );
    const HAS_CAT_83 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_83,
    );
    const HAS_CAT_84 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_84,
    );
    const HAS_CAT_85 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_85,
    );
    const HAS_CAT_86 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_86,
    );

    // If Category 801 is true then hide Category 800
    if (HAS_CAT_81) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_801,
      );
    } else if (HAS_CAT_8) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_800,
      );
    }

    if (
      (COMMON_800 && (HAS_CAT_34 || HAS_CAT_82)) ||
      (HAS_CAT_81 && HAS_CAT_82)
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_802,
      );
    }
    if ((COMMON_800 && HAS_CAT_83) || (HAS_CAT_81 && HAS_CAT_83)) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_803,
      );
    }
    if (HAS_CAT_84) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_804,
      );
    }

    if ((COMMON_800 && HAS_CAT_85) || (HAS_CAT_81 && HAS_CAT_85)) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_805,
      );
      if (HAS_CAT_86) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_806,
        );
      }
    }

    // Category 900s
    const HAS_CAT_91 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_91,
    );
    const HAS_CAT_92 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_92,
    );
    const HAS_CAT_93 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_93,
    );
    const HAS_CAT_94 = PossibleRootCauseCategory.hasAnyRootCauseCategories(
      this.possibleRootCauseCategories,
      RootCauseCategory.CATEGORY_94,
    );

    if (HAS_CAT_91 && this.ferritin.isGreaterThanOrIsOptimal()) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_901,
      );
    }

    if (HAS_CAT_92) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_902,
      );
    }

    if (HAS_CAT_93) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_903,
      );
    }

    if (HAS_CAT_94) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_904,
      );
    }

    if (
      this.isAnemiaProbabilityPossibleOrHigher() &&
      this.iron.isGreaterThanOptimal() &&
      this.ferritin.isStricklyLessThanOptimal() &&
      (
        this.copperRBCμgmL.isGreaterThanOptimal() ||
        this.copperRBCμgdL.isGreaterThanOptimal() ||
        this.copperSerumμgdL.isGreaterThanOptimal() ||
        this.copperSerummcgmL.isGreaterThanOptimal()
      )
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_949,
      );
      }

    if (
      this.isAnemiaProbabilityPossibleOrHigher() &&
      this.iron.isGreaterThanOptimal() &&
      this.ferritin.isStricklyLessThanOptimal() &&
      this.hsCRP.isEmptyOrIsLessThanOrIsOptimal()
    ) {
      this.possibleRootCauseCategories.push(
        PossibleRootCauseCategory.CATEGORY_950,
      );

      const zincMarkers = [
        this.zincRBCmgL,
        this.zincRBCμgdL,
        this.zincSerummcgmL,
        this.zincSerumμgdL,
      ].filter(Boolean);

      const hasAnyZincValue = zincMarkers.some(marker => marker.hasValue());
      const hasLowZinc = zincMarkers.some(
        marker => marker.hasValue() && marker.isStricklyLessThanOptimal(),
      );

      if (hasLowZinc) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_952,
        );
      } else if (!hasAnyZincValue && this.alp.isStricklyLessThanOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_951,
        );
      }
    }

    const CAT_955_960_RULE =
      PossibleRootCauseCategory.hasAnyRootCauseCategories(
        this.possibleRootCauseCategories,
        RootCauseCategory.CATEGORY_502,
        RootCauseCategory.CATEGORY_503,
        RootCauseCategory.CATEGORY_509,
        RootCauseCategory.CATEGORY_802,
        RootCauseCategory.CATEGORY_951,
        RootCauseCategory.CATEGORY_952,
      );

    // 955 and 960 Rules
    if (CAT_955_960_RULE) {
      if (this.wbc.isGreaterThanOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_955,
        );
      } else if (this.wbc.isStricklyLessThanOptimal()) {
        this.possibleRootCauseCategories.push(
          PossibleRootCauseCategory.CATEGORY_960,
        );
      }
    }
  }
  // iron BLANK OR >= Opt AND
  // tibc BLANK OR <= Opt AND
  // saturation BLANK OR > Opt AND
  // ferritin >= Opt
  private commonIronPanelEvaluation?(): boolean {
    return (
      (this.iron.isBlank() || this.iron.isGreaterThanOrIsOptimal()) &&
      (this.tibc.isBlank() || this.tibc.isLessThanOrIsOptimal()) &&
      (this.saturation.isBlank() || this.saturation.isGreaterThanOptimal()) &&
      this.ferritin.isGreaterThanOrIsOptimal()
    );
  }
}
